import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  CaretLeft,
  PencilSimple,
  DotsThree,
  Buildings,
  User,
  Shield,
  Warning,
  ArrowsClockwise,
  Phone,
  Users as UsersIcon,
  Fingerprint,
  ShieldCheck,
  Sparkle,
  Printer,
  Trash,
  Coins,
  Receipt,
  CaretDown,
  ArrowLineUpRight,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { reimprimirComprobantePorMovimientoId } from '@/lib/reimprimirComprobante'
import { imprimirEstadoCuentaAsociado } from '@/lib/estadoCuentaAsociado'
import { PAISES, PAIS_DEFAULT, detectarPais, digitosNacionales, formatearTelefonoDisplay, type Pais } from '@/lib/paises'
import type {
  AsociadoCompleto,
  EstadoAsociado,
  KycEstado,
  MovimientoCuentaListItem,
  PlazoFijoLite,
} from '@/lib/typesNeoCore'
import { CambiarEstadoModal } from '@/components/CambiarEstadoModal'
import { OfacPanel } from '@/components/OfacPanel'
import { BeneficiarioModal } from '@/components/BeneficiarioModal'
import { RevocarBeneficiarioModal } from '@/components/RevocarBeneficiarioModal'
import { EditarFeisSeccionModal, type FeisSeccion } from '@/components/EditarFeisSeccionModal'
import { EditarContactoSeccionModal, type ContactoSeccion } from '@/components/EditarContactoSeccionModal'
import { RegistrarInscripcionModal } from '@/components/RegistrarInscripcionModal'
import { ComprobanteAportePanel } from '@/components/ComprobanteAportePanel'
import { NuevaOperacionDrawer } from '@/components/NuevaOperacionDrawer'
import { SolicitarRetiroModal } from '@/components/SolicitarRetiroModal'
import { SolicitarCreditoDrawer } from '@/components/SolicitarCreditoDrawer'
import { EditarReferenciaBancariaModal } from '@/components/EditarReferenciaBancariaModal'
import { ActualizarFirmaModal } from '@/components/ActualizarFirmaModal'
import { RegistrarPagoCreditoDrawer } from '@/components/RegistrarPagoCreditoDrawer'
import { CredencialesAppPanel, type AppStatus } from '@/components/CredencialesAppPanel'
import { OkidVerificarModal } from '@/components/OkidVerificarModal'
import { fechaGT } from '../lib/fechas'

type Tab = 'resumen' | 'productos' | 'movimientos' | 'contacto' | 'beneficiarios' | 'cumplimiento' | 'historico'

// Sub-tabs dentro del módulo Cumplimiento
type CumplimientoSubtab = 'feis' | 'feic' | 'kyc' | 'documentos' | 'banderas' | 'cuenta_bancaria'

export function NeoCoreAsociadoFichaPage() {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<AsociadoCompleto | null>(null)
  const [plazosFijos, setPlazosFijos] = useState<PlazoFijoLite[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('resumen')
  const [cambioEstadoOpen, setCambioEstadoOpen] = useState(false)
  const [inscripcionOpen, setInscripcionOpen] = useState(false)
  const [nuevaOpOpen, setNuevaOpOpen] = useState(false)
  const [retiroOpen, setRetiroOpen] = useState(false)
  const [creditoOpen, setCreditoOpen] = useState(false)
  const [creditoTipo, setCreditoTipo] = useState('emergente_quincenal')
  const [creditoMenu, setCreditoMenu] = useState(false)
  const [firmaOpen, setFirmaOpen] = useState(false)
  const [okidOpen, setOkidOpen] = useState(false)

  const reload = useCallback(() => {
    if (!id) return
    setLoading(true)
    setError(null)
    Promise.all([
      supabase.rpc('obtener_asociado_completo', { p_asociado_id: id }),
      // Plazos fijos vivos (pendientes + activos + vencidos + renovados).
      // Excluimos cancelados/liquidados/rechazados — esos van al histórico.
      supabase
        .from('plazo_fijo_detalle')
        .select(
          'id, cuenta_id, certificado_no, capital_inicial, tasa_anual, plazo_dias, manejo_capitalizacion, fecha_apertura, fecha_inicio, fecha_vencimiento, estado_pf, cuenta:cuenta_id(saldo_total)',
        )
        .eq('asociado_id', id)
        .in('estado_pf', ['pendiente_de_caja', 'activo', 'vencido', 'renovado'])
        .order('fecha_apertura', { ascending: false }),
    ])
      .then(([resCompleto, resPfs]) => {
        if (resCompleto.error) {
          setError(resCompleto.error.message)
        } else {
          setData(resCompleto.data as AsociadoCompleto)
        }
        if (!resPfs.error) {
          const pfs: PlazoFijoLite[] = (resPfs.data ?? []).map((row: any) => ({
            id: row.id,
            cuenta_id: row.cuenta_id,
            certificado_no: row.certificado_no,
            capital_inicial: Number(row.capital_inicial),
            tasa_anual: Number(row.tasa_anual),
            plazo_dias: row.plazo_dias,
            manejo_capitalizacion: row.manejo_capitalizacion,
            fecha_apertura: row.fecha_apertura,
            fecha_inicio: row.fecha_inicio,
            fecha_vencimiento: row.fecha_vencimiento,
            estado_pf: row.estado_pf,
            saldo_total: Number(row.cuenta?.saldo_total ?? 0),
          }))
          setPlazosFijos(pfs)
        }
        setLoading(false)
      })
  }, [id])

  useEffect(() => {
    reload()
  }, [reload])

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--n-500)' }}>
        Cargando…
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackLink />
        <div className="error-banner" style={{ margin: 0 }}>
          {error}
        </div>
      </div>
    )
  }

  if (!data) return null

  const { asociado, contactabilidad, beneficiarios, estado_historico } = data

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <BackLink />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Header */}
        <div className="ficha-header">
          <div className="avatar-lg">{iniciales(asociado.nombre_completo)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <h2 className="ficha-title">{asociado.nombre_completo}</h2>
              <EstadoBadgeFicha estado={asociado.estado_asociado} />
              {asociado.transacciones_bloqueadas && (
                <span
                  className="badge"
                  title={asociado.bloqueo_detalle ?? 'Salidas bloqueadas'}
                  style={{ background: 'var(--brand-50, #FFF1EB)', color: 'var(--brand-700, #C2410C)' }}
                >
                  <Warning size={10} weight="bold" style={{ marginRight: 4 }} />
                  {asociado.bloqueo_motivo === 'recibo_vencido' ? 'Recibo vencido · sin salidas' : 'Salidas bloqueadas'}
                </span>
              )}
              {asociado.requiere_feic && (
                <span className="badge badge-info">
                  <Shield size={10} weight="bold" style={{ marginRight: 4 }} />
                  Requiere FEIC
                </span>
              )}
              <span className="badge badge-muted">
                {asociado.tipo_persona === 'JURIDICA' ? (
                  <><Buildings size={10} weight="bold" style={{ marginRight: 4 }} />Jurídica</>
                ) : (
                  <><User size={10} weight="bold" style={{ marginRight: 4 }} />Individual</>
                )}
              </span>
            </div>
            <p className="ficha-meta">
              {/* neoID = número de asociado visible (6 dígitos memorables).
                  El `no_asociado` legacy (formato 2025-000002) queda solo
                  para documentos regulatorios FEIS/FEIC. */}
              <span className="mono val-strong">{asociado.neo_id || asociado.no_asociado}</span>
              <span className="meta-sep">·</span>
              <span>DPI <span className="mono">{fmtDpi(asociado.dpi)}</span></span>
              {asociado.onboarding_origen && (
                <>
                  <span className="meta-sep">·</span>
                  <span>vía {asociado.onboarding_origen}</span>
                </>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-start', flexWrap: 'wrap' }}>
            {/* CTA principal cuando es prospecto o pendiente: cobrar inscripción.
                'pendiente' se da cuando el asociado ya subió el comprobante por
                la app móvil — el operador verifica la foto contra el extracto y
                registra el pago acá. */}
            {(asociado.estado_asociado === 'prospecto' ||
              asociado.estado_asociado === 'pendiente') && (
              <button
                className="btn btn-primary"
                onClick={() => setInscripcionOpen(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Sparkle size={13} weight="bold" />
                {asociado.estado_asociado === 'pendiente'
                  ? 'Verificar y aprobar'
                  : 'Registrar pago de inscripción'}
              </button>
            )}

            {/* CTA principal cuando es activo: nueva operación */}
            {asociado.estado_asociado === 'activo' && (
              <button
                className="btn btn-primary"
                onClick={() => setNuevaOpOpen(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <Sparkle size={13} weight="bold" />
                Nueva operación
              </button>
            )}

            {/* Crédito emergente — botón secundario con el lenguaje de brand
                para destacarlo sin competir con "Nueva operación". */}
            {asociado.estado_asociado === 'activo' && (
              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setCreditoMenu((v) => !v)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 14px', borderRadius: 8,
                    background: 'var(--brand-soft, #ffe4d4)',
                    color: 'var(--brand-deep, #c43c00)',
                    border: '1px solid rgba(196,60,0,0.20)',
                    fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  }}
                >
                  <Coins size={13} weight="bold" /> Nuevo crédito ▾
                </button>
                {creditoMenu && (
                  <>
                    <div onClick={() => setCreditoMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                    <div style={{
                      position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 41, minWidth: 200,
                      background: '#fff', border: '1px solid var(--hairline)', borderRadius: 10,
                      boxShadow: '0 12px 30px rgba(0,0,0,.12)', overflow: 'hidden',
                    }}>
                      {[
                        { codigo: 'emergente_quincenal', label: 'Crédito Emergente', sub: 'Q300–2,500 · quincenal' },
                        { codigo: 'consumo', label: 'Crédito Consumo', sub: 'Q2,000–25,000 · mensual' },
                      ].map((op) => (
                        <button
                          key={op.codigo}
                          onClick={() => { setCreditoTipo(op.codigo); setCreditoMenu(false); setCreditoOpen(true) }}
                          style={{
                            display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                            padding: '10px 14px', border: 0, background: 'transparent',
                            borderBottom: '1px solid var(--line-soft)',
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ink)' }}>{op.label}</div>
                          <div style={{ fontSize: 11, color: 'var(--muted-foreground)' }}>{op.sub}</div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Solicitar retiro por transferencia → cae en la bandeja de
                Solicitudes de Retiros (camino correcto, NO usar Nueva operación
                → Transferencia para retiros de asociado). */}
            {asociado.estado_asociado === 'activo' && (
              <button
                onClick={() => setRetiroOpen(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 8,
                  background: 'var(--soft-gray, #f1f5f9)',
                  color: 'var(--ink, #0f172a)',
                  border: '1px solid var(--hairline)',
                  fontSize: 13, fontWeight: 700, cursor: 'pointer',
                }}
              >
                <ArrowLineUpRight size={13} weight="bold" /> Solicitar retiro
              </button>
            )}

            {/* Verificar identidad contra RENAP (OKID) */}
            <button
              onClick={() => setOkidOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 8,
                background: 'var(--soft-gray, #f1f5f9)',
                color: 'var(--ink, #0f172a)',
                border: '1px solid var(--hairline)',
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
              }}
            >
              <ShieldCheck size={13} weight="bold" /> Verificar identidad
            </button>

            {/* Menú "Más ▾" — agrupa acciones de bajo uso (Editar, Cambiar estado)
                y acciones destructivas (QA Reversar, QA Eliminar) para no saturar
                el header con 8 botones. */}
            <FichaAccionesMenu
              estado={asociado.estado_asociado}
              onEstadoCuenta={async () => {
                const r = await imprimirEstadoCuentaAsociado(asociado.id)
                if (!r.ok) alert('No se pudo generar el estado de cuenta: ' + r.error)
              }}
              onCambiarEstado={() => setCambioEstadoOpen(true)}
              onActualizarFirma={() => setFirmaOpen(true)}
              onReversar={async () => {
                if (!confirm(`¿Reversar la inscripción de ${asociado.nombre_completo}?\n\nBorra cuentas + plazos fijos + reportes, anula movimiento de caja y vuelve a prospecto. La cuenta del asociado y su FEIS se mantienen.\n\nSolo para QA.`)) return
                const { error: revErr } = await supabase.rpc('reversar_inscripcion', {
                  p_asociado_id: asociado.id,
                  p_motivo: 'Reverso QA',
                })
                if (revErr) { alert('No se pudo reversar: ' + revErr.message); return }
                reload()
              }}
              onEliminar={async () => {
                const sure = confirm(
                  `¿ELIMINAR completamente a ${asociado.nombre_completo}?\n\n` +
                  `Borra TODO: cuentas, plazos fijos, FEIS, KYC, contactabilidad, ` +
                  `beneficiarios, biométrico, audit logs y la cuenta de login (auth user).\n\n` +
                  `No tiene vuelta atrás. Solo para QA.`,
                )
                if (!sure) return
                const { data, error: delErr } = await supabase.rpc('eliminar_asociado_qa', {
                  p_dpi: asociado.dpi,
                })
                if (delErr) {
                  alert('No se pudo eliminar: ' + delErr.message)
                  return
                }
                if (data && (data as { found: boolean }).found === false) {
                  alert('No se encontró el asociado por DPI.')
                  return
                }
                window.location.assign('/neocore/asociados')
              }}
            />
          </div>
        </div>

        {/* Tabs centradas en el negocio del asociado.
            Cumplimiento encapsula todo lo regulatorio (FEIS / FEIC / KYC / Documentos / Banderas). */}
        <div className="ficha-tabs">
          {(
            [
              ['resumen', 'Resumen', null],
              // Conteo = saldos reales (aportaciones + ahorros + plazos fijos vivos).
              // Antes leíamos feis_productos (snapshot regulatorio) — ahora la
              // métrica refleja productos contratados de verdad.
              ['productos', 'Productos', (() => {
                const n =
                  (data.cuenta_aportaciones ? 1 : 0) +
                  (data.cuentas_ahorro ?? []).length +
                  plazosFijos.length
                return n > 0 ? String(n) : null
              })()],
              ['movimientos', 'Movimientos', null],
              ['contacto', 'Contactabilidad', null],
              ['beneficiarios', 'Beneficiarios', beneficiarios.length > 0 ? String(beneficiarios.length) : null],
              ['cumplimiento', 'Cumplimiento', cumplimientoBadge(data)],
              ['historico', 'Histórico', estado_historico.length > 0 ? String(estado_historico.length) : null],
            ] as Array<[Tab, string, string | null]>
          ).map(([key, label, count]) => (
            <button
              key={key}
              className={'tab-btn' + (tab === key ? ' active' : '')}
              onClick={() => setTab(key)}
            >
              {label}
              {count !== null && (
                <span className="tab-count">{count}</span>
              )}
            </button>
          ))}
        </div>

        {/* Contenido */}
        <div style={{ padding: '20px 24px' }}>
          {tab === 'resumen' && (
            <ResumenTab
              data={data}
              plazosFijos={plazosFijos}
              onGoToTab={setTab}
              onRegistrarInscripcion={() => setInscripcionOpen(true)}
              onChange={reload}
            />
          )}
          {tab === 'productos' && (
            <ProductosTab
              asociadoId={asociado.id}
              productos={data.feis_productos ?? []}
              ctaApo={data.cuenta_aportaciones}
              ctasAho={data.cuentas_ahorro ?? []}
              plazosFijos={plazosFijos}
            />
          )}
          {tab === 'movimientos' && (
            <MovimientosTab
              asociadoId={asociado.id}
              nombreCompleto={asociado.nombre_completo}
              movimientos={data.movimientos_cuenta ?? []}
              onReload={reload}
            />
          )}
          {tab === 'contacto' && <ContactoTab c={contactabilidad} asociadoId={asociado.id} onChange={reload} />}
          {tab === 'beneficiarios' && (
            <BeneficiariosTab
              list={beneficiarios}
              asociadoId={asociado.id}
              requiereFeic={asociado.requiere_feic}
              onChange={reload}
            />
          )}
          {tab === 'cumplimiento' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <OfacPanel asociadoId={asociado.id} nombreCompleto={asociado.nombre_completo} />
              <CumplimientoTab data={data} onChange={reload} />
            </div>
          )}
          {tab === 'historico' && <HistoricoTab estados={estado_historico} feis={data.feis_historico ?? []} />}
        </div>
      </div>

      <CambiarEstadoModal
        open={cambioEstadoOpen}
        onClose={() => setCambioEstadoOpen(false)}
        asociadoId={asociado.id}
        nombreCompleto={asociado.nombre_completo}
        estadoActual={asociado.estado_asociado}
        onSuccess={reload}
      />

      <RegistrarInscripcionModal
        open={inscripcionOpen}
        onClose={() => { setInscripcionOpen(false); reload() }}
        asociadoId={asociado.id}
        nombreCompleto={asociado.nombre_completo}
        noAsociado={asociado.no_asociado}
        neoId={asociado.neo_id}
        dpi={asociado.dpi}
        onSuccess={() => { /* reload se hace en onClose */ }}
      />

      <NuevaOperacionDrawer
        open={nuevaOpOpen}
        onClose={() => { setNuevaOpOpen(false); reload() }}
        asociadoId={asociado.id}
        nombreCompleto={asociado.nombre_completo}
        noAsociado={asociado.no_asociado}
        neoId={asociado.neo_id}
        dpi={asociado.dpi}
        referenciaBancaria={data.referencia_bancaria}
        onSuccess={() => { /* reload se hace en onClose */ }}
      />

      <SolicitarRetiroModal
        open={retiroOpen}
        onClose={() => { setRetiroOpen(false); reload() }}
        asociadoId={asociado.id}
        asociadoNombre={asociado.nombre_completo}
        referencia={data.referencia_bancaria}
        saldoAhorro={(data.cuentas_ahorro ?? []).reduce((s, c: any) => s + Number(c?.saldo ?? 0), 0)}
        onSuccess={() => { /* reload se hace en onClose */ }}
      />

      <SolicitarCreditoDrawer
        open={creditoOpen}
        onClose={() => { setCreditoOpen(false); reload() }}
        asociadoId={asociado.id}
        nombreCompleto={asociado.nombre_completo}
        noAsociado={asociado.no_asociado}
        tipoCodigo={creditoTipo}
        onSuccess={() => { /* reload se hace en onClose */ }}
      />

      <ActualizarFirmaModal
        open={firmaOpen}
        onClose={() => setFirmaOpen(false)}
        asociadoId={asociado.id}
        nombreCompleto={asociado.nombre_completo}
        noAsociado={asociado.no_asociado}
      />

      {okidOpen && (
        <OkidVerificarModal
          asociadoId={asociado.id}
          asociadoNombre={asociado.nombre_completo}
          cui={asociado.dpi}
          onClose={() => setOkidOpen(false)}
        />
      )}
    </div>
  )
}

// =============================================================
// FichaAccionesMenu — dropdown "Más ▾" con acciones secundarias.
// Mantiene el header limpio: solo las acciones primarias quedan
// visibles. Editar, Cambiar estado y QA viven acá adentro.
// =============================================================

function FichaAccionesMenu({
  estado, onEstadoCuenta, onCambiarEstado, onActualizarFirma, onReversar, onEliminar,
}: {
  estado: EstadoAsociado
  onEstadoCuenta: () => void | Promise<void>
  onCambiarEstado: () => void
  onActualizarFirma: () => void
  onReversar: () => void | Promise<void>
  onEliminar: () => void | Promise<void>
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-ghost"
        onClick={() => setOpen((o) => !o)}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        Más <DotsThree size={14} weight="bold" />
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', right: 0,
            minWidth: 240, padding: 6,
            background: '#fff', border: '1px solid var(--hairline)',
            borderRadius: 10, boxShadow: '0 8px 24px rgba(15,18,25,0.10)',
            zIndex: 50,
            display: 'flex', flexDirection: 'column',
          }}
        >
          <MenuItem
            icon={<Receipt size={14} weight="bold" />}
            label="Estado de cuenta"
            hint="Por producto · PDF"
            onClick={async () => { setOpen(false); await onEstadoCuenta() }}
          />
          <MenuDivider />
          <MenuItem
            icon={<PencilSimple size={14} weight="bold" />}
            label="Editar asociado"
            disabled
            hint="Próximamente"
          />
          {estado !== 'retirado' && (
            <MenuItem
              icon={<ArrowsClockwise size={14} weight="bold" />}
              label="Cambiar estado"
              onClick={() => { setOpen(false); onCambiarEstado() }}
            />
          )}
          {estado === 'activo' && (
            <MenuItem
              icon={<PencilSimple size={14} weight="bold" />}
              label="Actualizar firma"
              hint="Manuscrita"
              onClick={() => { setOpen(false); onActualizarFirma() }}
            />
          )}
          {estado === 'activo' && (
            <>
              <MenuDivider />
              <MenuItem
                icon={<ArrowsClockwise size={14} weight="bold" />}
                label="Reversar inscripción"
                hint="QA · vuelve a prospecto"
                color="var(--warn-ink, #d97706)"
                onClick={async () => { setOpen(false); await onReversar() }}
              />
            </>
          )}
          <MenuDivider />
          <MenuItem
            icon={<Trash size={14} weight="bold" />}
            label="Eliminar asociado"
            hint="QA · sin vuelta atrás"
            color="var(--danger, #a32d2d)"
            onClick={async () => { setOpen(false); await onEliminar() }}
          />
        </div>
      )}
    </div>
  )
}

function MenuItem({
  icon, label, hint, color, disabled, onClick,
}: {
  icon: React.ReactNode
  label: string
  hint?: string
  color?: string
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      disabled={disabled}
      style={{
        all: 'unset',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 10px', borderRadius: 6,
        color: disabled ? 'var(--muted-foreground)' : (color ?? 'var(--ink)'),
        opacity: disabled ? 0.5 : 1,
        fontSize: 13,
      }}
      onMouseEnter={(e) => {
        if (!disabled) (e.currentTarget as HTMLButtonElement).style.background = 'var(--n-100, #f3efeb)'
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent'
      }}
    >
      <span style={{ display: 'inline-flex', width: 16, color: 'inherit' }}>{icon}</span>
      <span style={{ flex: 1, fontWeight: 600 }}>{label}</span>
      {hint && (
        <span style={{ fontSize: 10, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
          {hint}
        </span>
      )}
    </button>
  )
}

function MenuDivider() {
  return <div style={{ height: 1, background: 'var(--hairline)', margin: '4px 0' }} />
}

// =============================================================
// Tabs
// =============================================================

// =============================================================
// RESUMEN — dashboard de negocio del asociado
// =============================================================
// El asociado es nuestro centro de negocios. Esta vista debe responder
// de un vistazo: ¿qué tiene contratado? ¿cuánto maneja con nosotros?
// ¿cómo lo contactamos? ¿qué oportunidades / pendientes hay?
// El cumplimiento (FEIS/FEIC/KYC) NO vive acá — vive en su propio tab.
// =============================================================

function ResumenTab({
  data,
  plazosFijos,
  onGoToTab,
  onRegistrarInscripcion,
  onChange,
}: {
  data: AsociadoCompleto
  plazosFijos: PlazoFijoLite[]
  onGoToTab: (tab: Tab) => void
  onRegistrarInscripcion: () => void
  onChange?: () => void
}) {
  const { asociado, contactabilidad, ultimo_kyc, estado_historico } = data

  // Saldos REALES desde cuentas (fuente de verdad). El feis_productos
  // es el snapshot regulatorio del expediente IVE — NO la fuente de saldos.
  const ctaApo = data.cuenta_aportaciones
  const ctasAho = data.cuentas_ahorro ?? []

  // PFs vivos: el saldo viene de cuenta.saldo_total del JOIN.
  const totalPF = plazosFijos.reduce((s, p) => s + p.saldo_total, 0)
  const pfsPendientes = plazosFijos.filter((p) => p.estado_pf === 'pendiente_de_caja').length

  // PASIVOS (captación: la coop le debe al asociado)
  const totalPasivos =
    Number(ctaApo?.saldo ?? 0) +
    ctasAho.reduce((s, c) => s + Number(c.saldo), 0) +
    totalPF

  // ACTIVOS (cartera: el asociado le debe a la coop)
  // Lee créditos vigentes del asociado. saldo_pendiente = capital pendiente
  // + interés no cobrado + mora acumulada. Es lo que el operador necesita
  // ver para saber "cuánto nos debe en total".
  type CreditoResumen = { tipo: string; saldo: number; proximaCuota: number; proximoVence: string | null }
  const [creditos, setCreditos] = useState<CreditoResumen[]>([])
  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('credito')
        .select(`
          id, saldo_capital, fecha_ultima_cuota,
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado
          )
        `)
        .eq('asociado_id', asociado.id)
        .in('estado', ['vigente', 'en_mora', 'al_dia'])
      const rows: CreditoResumen[] = (data ?? []).map((r) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c = r as any
        const cuotasPend = (c.cuotas ?? []).filter(
          (q: { estado: string }) => q.estado !== 'pagada' && q.estado !== 'castigada',
        )
        const saldoPendiente = cuotasPend.reduce((s: number, q: {
          capital: number; interes: number; mora_acumulada: number
          capital_pagado: number; interes_pagado: number; mora_pagada: number
        }) => s +
          (Number(q.capital) - Number(q.capital_pagado)) +
          (Number(q.interes) - Number(q.interes_pagado)) +
          (Number(q.mora_acumulada) - Number(q.mora_pagada)), 0)
        const proxima = cuotasPend.sort(
          (a: { numero: number }, b: { numero: number }) => a.numero - b.numero,
        )[0]
        const proxMonto = proxima
          ? (Number(proxima.capital) - Number(proxima.capital_pagado)) +
            (Number(proxima.interes) - Number(proxima.interes_pagado)) +
            (Number(proxima.mora_acumulada) - Number(proxima.mora_pagada))
          : 0
        return {
          tipo: c.tipo?.nombre ?? 'Crédito',
          saldo: saldoPendiente,
          proximaCuota: proxMonto,
          proximoVence: proxima?.fecha_vencimiento ?? null,
        }
      })
      setCreditos(rows)
    })()
  }, [asociado.id])

  const totalActivos = creditos.reduce((s, c) => s + c.saldo, 0)

  // Desglose para las mini-bars del hero
  const saldosPasivosPorTipo: Array<[string, number]> = []
  if (ctaApo && Number(ctaApo.saldo) > 0) {
    saldosPasivosPorTipo.push(['APORTACIONES', Number(ctaApo.saldo)])
  }
  const totalAhorro = ctasAho.reduce((s, c) => s + Number(c.saldo), 0)
  if (totalAhorro > 0) {
    saldosPasivosPorTipo.push(['AHORRO_CORRIENTE', totalAhorro])
  }
  if (totalPF > 0) {
    saldosPasivosPorTipo.push(['PLAZO_FIJO', totalPF])
  }
  saldosPasivosPorTipo.sort(([, a], [, b]) => b - a)

  const productos = (data.feis_productos ?? []).filter((p) => p.estado === 'vigente')
  const sinProductos = !ctaApo && ctasAho.length === 0 && plazosFijos.length === 0
  void pfsPendientes // se referencia desde el tab Productos donde el badge se muestra
  void saldosPasivosPorTipo // legacy — el nuevo hero arma su propio desglose

  // Oportunidades / pendientes — calculadas client-side mientras módulos no existen
  const oportunidades = computarOportunidades(data, { onRegistrarInscripcion })

  // Canal preferido para contactar
  const wa = contactabilidad?.telefono_movil_e164 ?? contactabilidad?.telefono_movil
  const email = contactabilidad?.email_principal

  void productos // se usa solo cuando se navega al tab de productos
  void ultimo_kyc // se referencia desde el badge del tab Cumplimiento
  void estado_historico // se referencia desde el tab Histórico

  const primerNombre = asociado.nombre_completo.split(' ')[0]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Posición financiera del asociado con la coop · diseño moderno
          con hero de posición neta + cards de captación y cartera */}
      <PosicionFinancieraHero
        primerNombre={primerNombre}
        totalPasivos={totalPasivos}
        totalActivos={totalActivos}
        ctaApoSaldo={Number(ctaApo?.saldo ?? 0)}
        totalAhorro={totalAhorro}
        totalPF={totalPF}
        creditos={creditos}
        sinProductos={sinProductos}
        fechaIngreso={asociado.fecha_ingreso}
      />

      {/* Sección dedicada de Créditos · cards accionables.
          Hace visible la cartera desde el Resumen sin tener que ir a
          Productos. Cada card linkea al detalle y muestra próxima cuota
          + acceso directo al detalle. */}
      <CreditosResumenSeccion asociadoId={asociado.id} />

      {/* COMPROBANTE DE APORTE — solo cuando el asociado está pendiente.
          Si subió por la app vemos la foto; si vino por WhatsApp, mostramos
          un banner pidiendo verificar fuera de banda. El operador después
          aprueba con "Verificar y aprobar" en el header. */}
      {/* El comprobante de aporte queda SIEMPRE visible como respaldo de
          auditoría — aunque el asociado ya sea activo. Solo se oculta si
          nunca subió foto y no está pendiente (nada que mostrar). */}
      {(asociado.comprobante_aporte_url || asociado.estado_asociado === 'pendiente') && (
        <ComprobanteAportePanel comprobantePath={asociado.comprobante_aporte_url} />
      )}

      {/* CONTACTO — chips modernos con icons y hover sutil */}
      <ContactoRapidoChips wa={wa ?? null} email={email ?? null} />

      {/* OPORTUNIDADES — solo si hay pendientes accionables */}
      {oportunidades.length > 0 && (
        <div
          style={{
            padding: '22px 24px',
            borderRadius: 22,
            background: 'linear-gradient(135deg, #fff 0%, var(--brand-50) 100%)',
            border: '1px solid var(--hairline)',
            boxShadow: 'var(--shadow-sm)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Acento decorativo sutil */}
          <div
            style={{
              position: 'absolute', top: -60, right: -60, width: 180, height: 180,
              borderRadius: '50%', background: 'var(--brand-100)', opacity: 0.45,
              filter: 'blur(36px)', pointerEvents: 'none',
            }}
          />
          <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <p style={{
                margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
                color: 'var(--brand-700)', textTransform: 'uppercase',
              }}>
                Próximos pasos
              </p>
              <span style={{
                fontSize: 11, fontWeight: 700, color: 'var(--ink-soft)',
                fontFeatureSettings: '"tnum"',
              }}>
                {oportunidades.length} pendiente{oportunidades.length === 1 ? '' : 's'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {oportunidades.map((o, i) => (
                <OportunidadCard key={i} o={o} onGoToTab={onGoToTab} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ÚLTIMOS MOVIMIENTOS — el dato más buscado al abrir la fiche */}
      <UltimosMovimientosCuentaPreview movimientos={data.movimientos_cuenta ?? []} />

      {/* CREDENCIALES DEL APP — regenerar / bloquear / desvincular PIN */}
      <CredencialesAppCard asociado={asociado} contactabilidad={contactabilidad} onChange={onChange} />
    </div>
  )
}

// ----- Card del panel de credenciales del app, montada al final del Resumen -----
function CredencialesAppCard({
  asociado,
  contactabilidad,
  onChange,
}: {
  asociado: AsociadoCompleto['asociado']
  contactabilidad: AsociadoCompleto['contactabilidad']
  onChange?: () => void
}) {
  // Estado app: si tiene auth_user_id, ya tiene credencial activa.
  // Si no, no ha sido invitado.
  const appStatus: AppStatus = asociado.auth_user_id ? 'activo' : 'no_invitado'
  const telefonoPreferido =
    contactabilidad?.telefono_movil_e164 ?? asociado.telefono_e164 ?? null

  const [editarTelOpen, setEditarTelOpen] = useState(false)

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--hairline)',
      borderRadius: 14,
      padding: '22px 24px',
      marginTop: 18,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Fingerprint size={18} color="var(--brand)" weight="duotone" />
        <h3 style={{
          fontSize: 14,
          fontWeight: 700,
          color: 'var(--ink)',
          letterSpacing: -0.01,
          margin: 0,
        }}>
          Credenciales del app móvil
        </h3>
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '0 0 16px' }}>
        Regenerá el PIN del asociado, bloqueá su acceso, o desvinculá la cuenta. El PIN nuevo se muestra una sola vez y se puede enviar por WhatsApp.
      </p>

      {/* Bloque: teléfono de login del app */}
      <div style={{
        background: 'var(--bg-soft, #FBF7F3)',
        border: '1px solid var(--hairline)',
        borderRadius: 10,
        padding: '12px 14px',
        marginBottom: 14,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <Phone size={16} color="var(--brand)" weight="duotone" />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
              Teléfono / usuario del app
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink)', fontVariant: 'tabular-nums' as const }}>
              {formatearTelefonoDisplay(asociado.telefono_e164 ?? null)}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setEditarTelOpen(true)}
          style={{
            all: 'unset',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 12px',
            borderRadius: 8,
            background: '#fff',
            border: '1px solid var(--hairline)',
            color: 'var(--brand)',
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <PencilSimple size={13} weight="bold" />
          Editar teléfono
        </button>
      </div>

      <CredencialesAppPanel
        asociadoId={asociado.id}
        noAsociado={asociado.no_asociado}
        nombreCompleto={asociado.nombre_completo}
        appStatus={appStatus}
        telefonoPreferido={telefonoPreferido}
        inviteSentAt={null}
      />

      <EditarTelefonoAppModal
        open={editarTelOpen}
        onClose={() => setEditarTelOpen(false)}
        asociadoId={asociado.id}
        telefonoActual={asociado.telefono_e164 ?? null}
        onSuccess={() => {
          setEditarTelOpen(false)
          onChange?.()
        }}
      />
    </div>
  )
}

// Catálogo de países + helpers viene del shared lib para reutilizarlo desde
// otros modals de admin (migración COBASY, wizard de inscripción, etc.).

// ----- Modal para editar teléfono/usuario de app del asociado (multi-país) -----
function EditarTelefonoAppModal({
  open, onClose, asociadoId, telefonoActual, onSuccess,
}: {
  open: boolean
  onClose: () => void
  asociadoId: string
  telefonoActual: string | null
  onSuccess: () => void
}) {
  const paisInicial = useMemo(() => detectarPais(telefonoActual), [telefonoActual])
  const draftInicial = useMemo(() => digitosNacionales(telefonoActual, paisInicial), [telefonoActual, paisInicial])

  const [pais, setPais] = useState<Pais>(paisInicial)
  const [draft, setDraft] = useState(draftInicial)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setPais(paisInicial)
      setDraft(draftInicial)
      setErr(null)
    }
  }, [open, paisInicial, draftInicial])

  if (!open) return null

  const largoOk = draft.length >= pais.minLen && draft.length <= pais.maxLen
  const soloDigitos = /^\d*$/.test(draft)
  const valido = largoOk && soloDigitos
  const telefonoE164 = `+${pais.prefijoDigits}${draft}`
  const cambio = telefonoE164 !== (telefonoActual ?? '')

  const guardar = async () => {
    if (!valido) {
      setErr(`El número debe tener ${pais.minLen === pais.maxLen ? pais.minLen : `${pais.minLen}-${pais.maxLen}`} dígitos`)
      return
    }
    setSaving(true)
    setErr(null)
    const { error } = await supabase.rpc('actualizar_telefono_asociado_admin', {
      p_asociado_id: asociadoId,
      p_telefono_e164: telefonoE164,
    })
    setSaving(false)
    if (error) {
      setErr(error.message)
      return
    }
    onSuccess()
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff',
          borderRadius: 16,
          padding: 24,
          width: 'min(460px, 100%)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: 'var(--ink)' }}>
          Editar teléfono del app
        </h3>
        <p style={{ margin: '6px 0 18px', fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          El teléfono es también el <strong>usuario</strong> con el que el asociado se loguea. Cambiarlo actualiza el email de login automáticamente.
        </p>

        {/* País + número */}
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
          País
        </label>
        <select
          value={pais.iso}
          onChange={(e) => {
            const p = PAISES.find((x) => x.iso === e.target.value) ?? PAIS_DEFAULT
            setPais(p)
            setDraft('') // limpia al cambiar de país para no dejar dígitos de otro largo
            setErr(null)
          }}
          style={{
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--hairline)',
            borderRadius: 10,
            fontSize: 14,
            background: '#fff',
            color: 'var(--ink)',
            marginBottom: 12,
          }}
        >
          {PAISES.map((p) => (
            <option key={p.iso} value={p.iso}>
              {p.bandera} {p.nombre} (+{p.prefijoDigits})
            </option>
          ))}
        </select>

        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: 6 }}>
          Número
        </label>
        <div style={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0,
          border: '1px solid var(--hairline)',
          borderRadius: 10,
          overflow: 'hidden',
          marginBottom: err ? 6 : 14,
        }}>
          <div style={{
            padding: '10px 12px',
            background: 'var(--bg-soft, #FBF7F3)',
            color: 'var(--text-secondary)',
            fontSize: 13,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span>{pais.bandera}</span>
            <span>+{pais.prefijoDigits}</span>
          </div>
          <input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value.replace(/\D/g, '').slice(0, pais.maxLen))
              setErr(null)
            }}
            placeholder={pais.placeholder}
            inputMode="numeric"
            style={{
              border: 0,
              outline: 0,
              padding: '10px 12px',
              fontSize: 15,
              flex: 1,
              fontVariant: 'tabular-nums' as const,
            }}
          />
        </div>

        {err && (
          <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--danger, #a32d2d)' }}>
            {err}
          </p>
        )}

        <div style={{
          background: 'var(--brand-50, #FFF5F0)',
          borderRadius: 8,
          padding: 12,
          fontSize: 11.5,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
          marginBottom: 16,
        }}>
          Al guardar, avisá al asociado del cambio. Su nueva credencial de login será <strong>{valido ? telefonoE164 : `+${pais.prefijoDigits} ${'•'.repeat(pais.maxLen)}`}</strong>. La contraseña <strong>no cambia</strong> con esta operación.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              all: 'unset',
              cursor: 'pointer',
              padding: '10px 16px',
              borderRadius: 10,
              background: 'var(--surface)',
              border: '1px solid var(--hairline)',
              color: 'var(--ink-soft)',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={saving || !valido || !cambio}
            style={{
              all: 'unset',
              cursor: saving || !valido || !cambio ? 'not-allowed' : 'pointer',
              padding: '10px 16px',
              borderRadius: 10,
              background: saving || !valido || !cambio ? 'var(--n-300)' : 'var(--brand-600)',
              color: '#fff',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {saving ? 'Guardando…' : 'Guardar cambio'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ----- Tipo de oportunidad / pendiente que se muestra en el resumen -----
type Oportunidad = {
  kind: 'warn' | 'opp' | 'info'
  Icon: PhosphorIcon
  titulo: string
  descripcion: string
  cta?: { label: string; tab?: Tab; onClick?: () => void }
}

function computarOportunidades(
  data: AsociadoCompleto,
  handlers: { onRegistrarInscripcion: () => void }
): Oportunidad[] {
  const opps: Oportunidad[] = []
  const { asociado, ultimo_kyc, contactabilidad, beneficiarios } = data

  // Asociado en estado prospecto = pendiente de pagar inscripción
  if (asociado.estado_asociado === 'prospecto') {
    opps.push({
      kind: 'warn',
      Icon: Warning,
      titulo: 'Pendiente: pago de inscripción',
      descripcion: 'Cuota de ingreso + aportación inicial. Al registrarlo en Caja se activa al asociado y se abren sus cuentas.',
      cta: { label: 'Cobrar e inscribir', onClick: handlers.onRegistrarInscripcion },
    })
  }

  // KYC pendiente o en review
  if (!ultimo_kyc || ultimo_kyc.estado === 'review') {
    opps.push({
      kind: 'warn',
      Icon: Fingerprint,
      titulo: ultimo_kyc ? 'KYC en revisión' : 'Falta verificación KYC',
      descripcion: 'La verificación de identidad es requerida antes de operar. Va al tab Cumplimiento.',
      cta: { label: 'Ver KYC', tab: 'cumplimiento' },
    })
  }

  // Vigencia DPI próxima a vencer (60 días)
  if (ultimo_kyc?.vigencia_documento) {
    // DATE 'YYYY-MM-DD': anclar mediodía para no correrse un día en GT (UTC-6)
    const vence = new Date(String(ultimo_kyc.vigencia_documento).slice(0, 10) + 'T12:00:00')
    const dias = Math.ceil((vence.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    if (dias > 0 && dias <= 60) {
      opps.push({
        kind: 'warn',
        Icon: Warning,
        titulo: `DPI vence en ${dias} días`,
        descripcion: `Vigencia del documento: ${fmtFecha(ultimo_kyc.vigencia_documento)}. Coordinar renovación con el asociado.`,
      })
    } else if (dias <= 0) {
      opps.push({
        kind: 'warn',
        Icon: Warning,
        titulo: 'DPI vencido',
        descripcion: 'El documento del asociado venció. Solicitar renovación antes de cualquier operación.',
      })
    }
  }

  // Sin contactabilidad
  if (!contactabilidad || (!contactabilidad.email_principal && !contactabilidad.telefono_movil_e164)) {
    opps.push({
      kind: 'opp',
      Icon: Phone,
      titulo: 'Capturar canal de contacto',
      descripcion: 'No hay email ni teléfono registrado — sin esto no podemos avisarle de movimientos ni promociones.',
      cta: { label: 'Agregar contacto', tab: 'contacto' },
    })
  }

  // Sin beneficiarios designados (oportunidad)
  if (beneficiarios.length === 0) {
    opps.push({
      kind: 'opp',
      Icon: UsersIcon,
      titulo: 'Sin beneficiarios designados',
      descripcion: 'Sugerile al asociado designar a quién le quedaría su saldo en caso de fallecimiento.',
      cta: { label: 'Agregar beneficiario', tab: 'beneficiarios' },
    })
  }

  // App móvil — todavía no tenemos forma de saber si la instaló
  // Lo dejamos como "opp" tipo banner para validar idea
  // (cuando exista módulo Sucursal Digital esto se vuelve dato real)
  if (asociado.estado_asociado === 'activo' && !contactabilidad?.push_optin) {
    opps.push({
      kind: 'opp',
      Icon: Phone,
      titulo: 'Invitar a la app móvil',
      descripcion: 'No habilitó notificaciones push — invitarlo a instalar la app de Neocoop para acceso digital.',
    })
  }

  return opps
}

function OportunidadCard({ o, onGoToTab }: { o: Oportunidad; onGoToTab: (t: Tab) => void }) {
  const colors = {
    warn: { stripe: 'var(--warning, #d97706)', iconBg: 'var(--warning-50, #fff7ed)', iconFg: 'var(--warning, #d97706)', accent: 'var(--warning, #d97706)' },
    opp:  { stripe: 'var(--brand-600)', iconBg: 'var(--brand-100)', iconFg: 'var(--brand-700)', accent: 'var(--brand-700)' },
    info: { stripe: 'var(--n-300)', iconBg: 'var(--n-100)', iconFg: 'var(--n-600)', accent: 'var(--ink-soft)' },
  }[o.kind]
  const [hover, setHover] = useState(false)

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'flex', gap: 14, alignItems: 'center',
        padding: '14px 16px 14px 18px',
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        transition: 'transform 140ms ease, box-shadow 140ms ease, border-color 140ms ease',
        transform: hover ? 'translateY(-1px)' : 'none',
        boxShadow: hover ? 'var(--shadow-sm)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Banda lateral según tono */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: 0, width: 3,
        background: colors.stripe,
      }} />

      <div style={{
        width: 38, height: 38, borderRadius: 12,
        background: colors.iconBg, color: colors.iconFg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <o.Icon size={20} weight="duotone" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13.5, fontWeight: 700, margin: 0, color: 'var(--ink)' }}>{o.titulo}</p>
        <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: '3px 0 0', lineHeight: 1.5 }}>{o.descripcion}</p>
      </div>
      {o.cta && (
        <button
          type="button"
          onClick={() => {
            if (o.cta?.onClick) o.cta.onClick()
            else if (o.cta?.tab) onGoToTab(o.cta.tab)
          }}
          style={{
            all: 'unset', cursor: 'pointer',
            fontSize: 11, fontWeight: 700,
            letterSpacing: 0.5,
            color: '#fff',
            padding: '8px 14px',
            borderRadius: 999,
            background: colors.accent,
            whiteSpace: 'nowrap', alignSelf: 'center',
            boxShadow: hover ? '0 4px 12px rgba(0,0,0,0.08)' : 'none',
            transition: 'box-shadow 140ms ease, transform 140ms ease',
            transform: hover ? 'translateY(-1px)' : 'none',
          }}
        >
          {o.cta.label} →
        </button>
      )}
    </div>
  )
}

/**
 * Versión compacta de ContactoQuickAction — chip inline para el resumen.
 * Pensado para mostrar 3 canales (WhatsApp / Llamar / Email) sin ocupar
 * cards full-width. Si el canal no está disponible, queda atenuado y disabled.
 */
// ContactoChip — versión legacy del chip de contacto. Reemplazado por
// ContactoRapidoChips arriba. Lo dejamos disponible por reuso eventual
// en otras vistas (fiche del prospecto, listas, etc).
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function ContactoChip({
  disponible, label, href,
}: {
  disponible: boolean
  label: string
  href?: string
}) {
  const baseStyle: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '5px 10px',
    fontSize: 12, fontWeight: 600,
    borderRadius: 999,
    border: '1px solid var(--border)',
    background: disponible ? 'var(--surface)' : 'transparent',
    color: disponible ? 'var(--brand-600)' : 'var(--text-muted)',
    textDecoration: 'none',
    cursor: disponible ? 'pointer' : 'not-allowed',
    opacity: disponible ? 1 : 0.5,
    transition: 'background 120ms ease, border-color 120ms ease',
  }
  if (!disponible || !href) {
    return <span style={baseStyle}>{label}</span>
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={baseStyle}
      onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--brand-50)'; e.currentTarget.style.borderColor = 'var(--brand-200)' }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--surface)'; e.currentTarget.style.borderColor = 'var(--border)' }}
    >
      {label} →
    </a>
  )
}
void ContactoChip  // legacy disponible para reuso (lista/prospecto/etc.)

// =============================================================
// PRODUCTOS — vista detallada de las cuentas/créditos del asociado
// =============================================================

function ProductosTab({
  asociadoId, productos, ctaApo, ctasAho, plazosFijos,
}: {
  asociadoId: string
  productos: AsociadoCompleto['feis_productos']
  ctaApo: AsociadoCompleto['cuenta_aportaciones']
  ctasAho: AsociadoCompleto['cuentas_ahorro']
  plazosFijos: PlazoFijoLite[]
}) {
  const vigentes = (productos ?? []).filter((p) => p.estado === 'vigente')
  const tieneAlgo =
    vigentes.length > 0 ||
    !!ctaApo ||
    (ctasAho ?? []).length > 0 ||
    plazosFijos.length > 0
  if (!tieneAlgo) {
    return (
      <Empty>
        El asociado todavía no tiene productos. Las cuentas se crean
        automáticamente al registrar el pago de inscripción en Caja
        (cuenta de aportaciones + cuenta de ahorro corriente).
      </Empty>
    )
  }
  const pasivos = vigentes.filter((p) => esPasivo(p.tipo_producto))
  const activos = vigentes.filter((p) => esActivo(p.tipo_producto))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.5, maxWidth: 680 }}>
        Productos contratados con la cooperativa. Separamos captación
        (lo que el asociado tiene con nosotros) de cartera (lo que está pagando).
      </p>

      {pasivos.length > 0 && (
        <ProductoSeccion
          titulo="Captación"
          subtitulo="Cuentas donde el asociado mantiene su dinero"
          color="var(--brand-600)"
          productos={pasivos}
          ctaApo={ctaApo}
          ctasAho={ctasAho}
        />
      )}

      {plazosFijos.length > 0 && (
        <PlazosFijosSeccion plazosFijos={plazosFijos} />
      )}

      <CreditosSeccion asociadoId={asociadoId} />

      {activos.length > 0 && (
        <ProductoSeccion
          titulo="Cartera vigente"
          subtitulo="Créditos que el asociado tiene activos con la cooperativa"
          color="var(--brand-700)"
          productos={activos}
          ctaApo={ctaApo}
          ctasAho={ctasAho}
        />
      )}
    </div>
  )
}

/**
 * Sección de Créditos del asociado en el tab Productos.
 * Fetch independiente — no afecta el reload monolítico de la ficha.
 * Cada tarjeta linkea a /neocore/creditos/:id para el detalle completo.
 */
function CreditosSeccion({ asociadoId }: { asociadoId: string }) {
  type CreditoLite = {
    id: string
    tipo_nombre: string
    capital_inicial: number
    saldo_capital: number
    estado: string
    estado_provision: string | null
    fecha_desembolso: string
    fecha_ultima_cuota: string
    total_cuotas: number
  }
  const [list, setList] = useState<CreditoLite[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!asociadoId) return
    void (async () => {
      const { data } = await supabase
        .from('credito')
        .select(`
          id, capital_inicial, saldo_capital, estado, estado_provision,
          fecha_desembolso, fecha_ultima_cuota, total_cuotas,
          tipo:tipo_credito_id (nombre)
        `)
        .eq('asociado_id', asociadoId)
        .order('fecha_desembolso', { ascending: false })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rows: CreditoLite[] = (data ?? []).map((r: any) => ({
        id: r.id,
        tipo_nombre: r.tipo?.nombre ?? 'Crédito',
        capital_inicial: Number(r.capital_inicial),
        saldo_capital: Number(r.saldo_capital),
        estado: r.estado,
        estado_provision: r.estado_provision,
        fecha_desembolso: r.fecha_desembolso,
        fecha_ultima_cuota: r.fecha_ultima_cuota,
        total_cuotas: r.total_cuotas,
      }))
      setList(rows)
      setLoaded(true)
    })()
  }, [asociadoId])

  if (!loaded || list.length === 0) return null

  const fmtQ = (n: number) =>
    'Q ' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const fmtFecha = (iso: string) => {
    if (!iso) return '—'
    const d = new Date(String(iso).slice(0, 10) + 'T12:00:00')
    return d.toLocaleDateString('es-GT', { day: '2-digit', month: 'short', year: 'numeric' })
  }
  const totalDeuda = list.reduce((s, c) => s + c.saldo_capital, 0)

  return (
    <div>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: 12, marginBottom: 12,
      }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: 'var(--brand-700)', letterSpacing: 0.2 }}>
            Cartera vigente
          </h3>
          <p style={{ margin: '2px 0 0', fontSize: 12, color: 'var(--muted-foreground)' }}>
            Créditos que el asociado tiene activos con la cooperativa
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ margin: 0, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 1 }}>
            Saldo total pendiente
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 15, fontWeight: 800, color: 'var(--brand)', fontFeatureSettings: '"tnum"' }}>
            {fmtQ(totalDeuda)}
          </p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        {list.map((c) => (
          <Link
            key={c.id}
            to={`/neocore/creditos/${c.id}`}
            style={{
              textDecoration: 'none', color: 'inherit',
              borderRadius: 12, overflow: 'hidden',
              background: 'var(--surface)', border: '1px solid var(--hairline)',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 1px 2px rgba(20,23,28,0.04)',
            }}
          >
            {/* Banda brand tipo ticket */}
            <div style={{
              background: 'var(--brand)', color: '#fff',
              padding: '8px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                {c.tipo_nombre.toUpperCase()}
              </span>
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                background: 'rgba(255,255,255,0.20)',
                textTransform: 'uppercase', letterSpacing: 0.8,
              }}>
                {c.estado === 'cancelado' ? 'Cancelado'
                  : c.estado === 'en_mora' ? 'En mora'
                  : c.estado === 'castigado' ? 'Castigado'
                  : c.estado === 'vigente' ? '● Vigente'
                  : c.estado}
              </span>
            </div>
            {/* Cuerpo */}
            <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              <p style={{
                margin: 0, fontSize: 10, fontWeight: 700, color: 'var(--muted-foreground)',
                textTransform: 'uppercase', letterSpacing: 1.5,
              }}>
                Saldo pendiente
              </p>
              <p style={{
                margin: 0, fontSize: 26, fontWeight: 800,
                fontFeatureSettings: '"tnum"', color: 'var(--ink)',
              }}>
                {fmtQ(c.saldo_capital)}
              </p>
              <div style={{
                marginTop: 6, paddingTop: 8, borderTop: '1px dashed var(--line)',
                display: 'flex', justifyContent: 'space-between', fontSize: 11,
                color: 'var(--muted-foreground)',
              }}>
                <span>Capital <strong style={{ color: 'var(--ink)', fontFeatureSettings: '"tnum"' }}>{fmtQ(c.capital_inicial)}</strong></span>
                <span>{c.total_cuotas} cuotas</span>
              </div>
              <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--muted-foreground)' }}>
                {fmtFecha(c.fecha_desembolso)} → {fmtFecha(c.fecha_ultima_cuota)}
              </p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}

/**
 * Sección de Plazos Fijos en el tab Productos.
 * Lee de la tabla `plazo_fijo_detalle` + JOIN a `cuenta` (saldo real).
 * Distingue pendientes_de_caja (badge ámbar) de activos (saldo verde).
 * Cada tarjeta linkea a /neocore/plazos-fijos/:id para ver el detalle.
 */
function PlazosFijosSeccion({ plazosFijos }: { plazosFijos: PlazoFijoLite[] }) {
  const totalCapital = plazosFijos.reduce((s, p) => s + p.saldo_total, 0)
  const totalCompromiso = plazosFijos.reduce((s, p) => s + p.capital_inicial, 0)
  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h3
            style={{
              fontSize: 13,
              fontWeight: 700,
              margin: 0,
              color: 'var(--text-primary)',
              letterSpacing: -0.2,
            }}
          >
            Plazos fijos
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Depósitos a plazo con tasa preferencial
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p
            style={{
              fontSize: 11,
              color: 'var(--text-muted)',
              margin: 0,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Total invertido
          </p>
          <p
            style={{
              fontSize: 18,
              fontWeight: 700,
              margin: '2px 0 0',
              color: 'var(--brand-600)',
              fontFeatureSettings: '"tnum"',
            }}
          >
            {fmtMoneda(totalCapital)}
          </p>
          {totalCompromiso > totalCapital ? (
            <p style={{ fontSize: 11, color: '#B8860B', margin: '2px 0 0' }}>
              {fmtMoneda(totalCompromiso - totalCapital)} pendiente de ingreso
            </p>
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 12,
        }}
      >
        {plazosFijos.map((pf) => (
          <PlazoFijoCard key={pf.id} pf={pf} />
        ))}
      </div>
    </div>
  )
}

function PlazoFijoCard({ pf }: { pf: PlazoFijoLite }) {
  const isPendiente = pf.estado_pf === 'pendiente_de_caja'
  const monto = isPendiente ? pf.capital_inicial : pf.saldo_total
  const estadoLabel: Record<PlazoFijoLite['estado_pf'], string> = {
    solicitado: 'Solicitado',
    pendiente_de_caja: 'Pendiente de ingreso',
    activo: 'Activo',
    vencido: 'Vencido',
    renovado: 'Renovado',
    liquidado: 'Liquidado',
    cancelado_anticipado: 'Cancelado',
    rechazado: 'Rechazado',
  }
  const estadoColor = isPendiente
    ? '#B8860B'
    : pf.estado_pf === 'activo' || pf.estado_pf === 'renovado'
      ? '#0F8A57'
      : pf.estado_pf === 'vencido'
        ? '#D4A017'
        : '#888780'
  const tasaPct = (pf.tasa_anual * 100).toFixed(2).replace(/\.00$/, '')
  return (
    <Link
      to={`/neocore/plazos-fijos/${pf.id}`}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: 14,
        gap: 8,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color .15s, background .15s',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>
          {pf.certificado_no ?? 'Plazo fijo'}
        </div>
        <span
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: estadoColor,
          }}
        >
          {estadoLabel[pf.estado_pf]}
        </span>
      </div>
      <div
        style={{
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: -0.6,
          color: 'var(--text-primary)',
          fontFeatureSettings: '"tnum"',
        }}
      >
        {fmtMoneda(monto)}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
        {pf.plazo_dias} días · {tasaPct}% anual ·{' '}
        {pf.manejo_capitalizacion === 'MENSUAL' ? 'pago mensual' : 'al vencimiento'}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--text-muted)',
          paddingTop: 4,
          borderTop: '1px dashed var(--border)',
        }}
      >
        Vence {fmtFecha(pf.fecha_vencimiento)}
      </div>
    </Link>
  )
}

function ProductoSeccion({
  titulo, subtitulo, color, productos, ctaApo, ctasAho,
}: {
  titulo: string
  subtitulo: string
  color: string
  productos: AsociadoCompleto['feis_productos']
  ctaApo: AsociadoCompleto['cuenta_aportaciones']
  ctasAho: AsociadoCompleto['cuentas_ahorro']
}) {
  // Total con saldos reales — fallback al snapshot regulatorio del FEIS
  // cuando el producto no tiene cuenta real todavía (PLAZO_FIJO, CREDITO).
  const total = productos.reduce((s, p) => {
    const real = saldoRealDeProducto(p, ctaApo, ctasAho)
    return s + (real ?? Number(p.valor))
  }, 0)
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 style={{ fontSize: 13, fontWeight: 700, margin: 0, color: 'var(--text-primary)', letterSpacing: -0.2 }}>
            {titulo}
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0' }}>{subtitulo}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</p>
          <p style={{ fontSize: 18, fontWeight: 700, margin: '2px 0 0', color, fontFeatureSettings: '"tnum"' }}>{fmtMoneda(total)}</p>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
        {productos.map((p) => (
          <ProductoCard key={p.id} p={p} saldoReal={saldoRealDeProducto(p, ctaApo, ctasAho)} />
        ))}
      </div>
    </div>
  )
}

// =============================================================
// MOVIMIENTOS — placeholder hasta que arranque módulo Caja
// =============================================================

// Filtros del tab Movimientos · etiquetas que matchean los tipos del feed
type MovFiltro = 'todos' | 'ahorro_corriente' | 'aportaciones' | 'credito'

function MovimientosTab({
  asociadoId, nombreCompleto, movimientos, onReload,
}: {
  asociadoId: string
  nombreCompleto: string
  movimientos: Array<MovimientoCuentaListItem>
  onReload: () => void
}) {
  const [filtro, setFiltro] = useState<MovFiltro>('todos')

  // ── Atajo de cobro de cuota ───────────────────────────────────
  // Detecta créditos vigentes con cuotas pendientes. Si hay 1 → CTA
  // directo "Cobrar cuota". Si hay varios → dropdown.
  type CreditoCobrable = { id: string; tipoNombre: string; pendienteCuota: number; diasParaProx: number | null }
  const [cobrables, setCobrables] = useState<CreditoCobrable[]>([])
  const [pagoOpen, setPagoOpen] = useState(false)
  const [pagoCreditoId, setPagoCreditoId] = useState<string | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('credito')
        .select(`
          id,
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado, dias_atraso_actual
          )
        `)
        .eq('asociado_id', asociadoId)
        .in('estado', ['vigente', 'en_mora', 'al_dia'])
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const rows: CreditoCobrable[] = []
      for (const r of (data ?? [])) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c = r as any
        const pend = (c.cuotas ?? [])
          .filter((q: { estado: string }) => q.estado !== 'pagada' && q.estado !== 'castigada')
          .sort((a: { numero: number }, b: { numero: number }) => a.numero - b.numero)
        const prox = pend[0]
        if (!prox) continue
        const monto =
          (Number(prox.capital) - Number(prox.capital_pagado)) +
          (Number(prox.interes) - Number(prox.interes_pagado)) +
          (Number(prox.mora_acumulada) - Number(prox.mora_pagada))
        const v = new Date(String(prox.fecha_vencimiento).slice(0, 10) + 'T00:00:00')
        const dias = Math.round((v.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        rows.push({
          id: c.id,
          tipoNombre: c.tipo?.nombre ?? 'Crédito',
          pendienteCuota: monto,
          diasParaProx: dias,
        })
      }
      setCobrables(rows)
    })()
  }, [asociadoId, movimientos.length])

  // Cierre del picker con click-outside / Escape
  useEffect(() => {
    if (!pickerOpen) return
    const onDown = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) setPickerOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPickerOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  const abrirCobro = (creditoId: string) => {
    setPagoCreditoId(creditoId)
    setPagoOpen(true)
    setPickerOpen(false)
  }

  // Conteos por tipo para mostrar en los tabs de filtro
  const counts = useMemo(() => ({
    todos: movimientos.length,
    ahorro_corriente: movimientos.filter((m) => m.tipo_cuenta === 'ahorro_corriente').length,
    aportaciones: movimientos.filter((m) => m.tipo_cuenta === 'aportaciones').length,
    credito: movimientos.filter((m) => m.tipo_cuenta === 'credito').length,
  }), [movimientos])

  const list = useMemo(() => {
    if (filtro === 'todos') return movimientos
    return movimientos.filter((m) => m.tipo_cuenta === filtro)
  }, [movimientos, filtro])

  // Totales del feed visible (informativo) — abonos vs cargos
  const totales = useMemo(() => {
    let abonos = 0, cargos = 0
    for (const m of list) {
      const v = Number(m.monto)
      if (v > 0) abonos += v
      else cargos += Math.abs(v)
    }
    return { abonos, cargos }
  }, [list])

  if (movimientos.length === 0 && cobrables.length === 0) {
    return (
      <Empty>
        Aún no hay movimientos en las cuentas del asociado. Cuando se
        registre un depósito, retiro o pago de crédito desde Caja, aparece acá
        con su correlativo INGECOP.
      </Empty>
    )
  }

  const filtroTabs: Array<{ key: MovFiltro; label: string }> = [
    { key: 'todos',            label: 'Todos' },
    { key: 'ahorro_corriente', label: 'Ahorro' },
    { key: 'aportaciones',     label: 'Aportaciones' },
    { key: 'credito',          label: 'Créditos' },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header del tab: contexto + CTA cobro */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 14px',
        background: 'var(--bg)', border: '1px dashed var(--border)',
        borderRadius: 10,
      }}>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Movimientos del asociado.</strong>{' '}
          Feed unificado de operaciones sobre sus cuentas (depósitos / retiros) y pagos a la
          cartera de créditos. Cada row trae su correlativo INGECOP y se puede reimprimir.
        </div>

        {/* Atajo · Cobrar cuota — solo si hay créditos pendientes */}
        {cobrables.length === 1 && (
          <button
            onClick={() => abrirCobro(cobrables[0].id)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '8px 14px', borderRadius: 10,
              background: 'var(--brand)', color: '#fff',
              border: 0, cursor: 'pointer',
              fontSize: 13, fontWeight: 700,
              boxShadow: '0 1px 2px rgba(20,23,28,0.04)',
            }}
          >
            <Receipt size={14} weight="bold" />
            Cobrar cuota · {fmtMoneda(cobrables[0].pendienteCuota)}
            {cobrables[0].diasParaProx !== null && cobrables[0].diasParaProx <= 3 && (
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '1px 7px', borderRadius: 99,
                background: 'rgba(255,255,255,0.22)',
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {cobrables[0].diasParaProx < 0 ? 'Vencida' : cobrables[0].diasParaProx === 0 ? 'Hoy' : `${cobrables[0].diasParaProx}d`}
              </span>
            )}
          </button>
        )}

        {cobrables.length > 1 && (
          <div ref={pickerRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setPickerOpen((o) => !o)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '8px 14px', borderRadius: 10,
                background: 'var(--brand)', color: '#fff',
                border: 0, cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
              }}
              aria-haspopup="menu"
              aria-expanded={pickerOpen}
            >
              <Receipt size={14} weight="bold" />
              Cobrar cuota
              <span style={{
                fontSize: 9, fontWeight: 800, padding: '1px 7px', borderRadius: 99,
                background: 'rgba(255,255,255,0.22)',
                textTransform: 'uppercase', letterSpacing: 0.5,
              }}>
                {cobrables.length}
              </span>
              <CaretDown size={11} weight="bold" />
            </button>
            {pickerOpen && (
              <div
                role="menu"
                style={{
                  position: 'absolute', top: 'calc(100% + 6px)', right: 0,
                  minWidth: 260, padding: 6,
                  background: '#fff', border: '1px solid var(--hairline)',
                  borderRadius: 10, boxShadow: '0 8px 24px rgba(15,18,25,0.10)',
                  zIndex: 50,
                  display: 'flex', flexDirection: 'column', gap: 2,
                }}
              >
                {cobrables.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => abrirCobro(c.id)}
                    style={{
                      all: 'unset', cursor: 'pointer',
                      padding: '8px 10px', borderRadius: 6,
                      display: 'flex', alignItems: 'center', gap: 8,
                      fontSize: 13,
                    }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--n-100)' }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent' }}
                  >
                    <span style={{ flex: 1, fontWeight: 600 }}>{c.tipoNombre}</span>
                    <span style={{
                      fontFeatureSettings: '"tnum"', fontWeight: 700,
                      color: 'var(--brand-700)',
                    }}>
                      {fmtMoneda(c.pendienteCuota)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtros + totales informativos */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 14px',
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        borderRadius: 12,
      }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {filtroTabs.map((f) => {
            const n = counts[f.key]
            const active = filtro === f.key
            return (
              <button
                key={f.key}
                onClick={() => setFiltro(f.key)}
                style={{
                  all: 'unset', cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '6px 12px', borderRadius: 99,
                  background: active ? 'var(--brand-50)' : 'transparent',
                  color: active ? 'var(--brand-700)' : 'var(--ink-soft)',
                  fontSize: 12, fontWeight: 700,
                  border: '1px solid',
                  borderColor: active ? 'var(--brand-300, transparent)' : 'transparent',
                  transition: 'background 0.12s ease',
                }}
              >
                {f.label}
                <span style={{
                  fontSize: 10, fontWeight: 800,
                  padding: '1px 6px', borderRadius: 99,
                  background: active ? 'var(--brand)' : 'var(--n-100)',
                  color: active ? '#fff' : 'var(--muted-foreground)',
                  fontFeatureSettings: '"tnum"',
                }}>
                  {n}
                </span>
              </button>
            )
          })}
        </div>

        <div style={{ flex: 1 }} />

        {/* Mini-totales */}
        {list.length > 0 && (
          <div style={{ display: 'flex', gap: 14, fontSize: 11 }}>
            <span style={{ color: 'var(--ink-soft)' }}>
              Abonos: <strong style={{ color: 'var(--ok-ink)', fontFeatureSettings: '"tnum"' }}>
                {fmtMoneda(totales.abonos)}
              </strong>
            </span>
            <span style={{ color: 'var(--ink-soft)' }}>
              Cargos: <strong style={{ color: 'var(--brand-700)', fontFeatureSettings: '"tnum"' }}>
                {fmtMoneda(totales.cargos)}
              </strong>
            </span>
          </div>
        )}
      </div>

      {/* Lista */}
      {list.length === 0 ? (
        <Empty>
          Sin movimientos de este tipo.
        </Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map((m) => <MovimientoCuentaRow key={m.id} m={m} />)}
        </div>
      )}

      {/* Nota al pie · enlace al feed completo de caja */}
      {movimientos.length >= 20 && (
        <p style={{ fontSize: 11, color: 'var(--muted-foreground)', textAlign: 'center', margin: '4px 0 0' }}>
          Se muestran los últimos 20 movimientos. Para auditoría completa visitá{' '}
          <Link to="/neocore/caja" style={{ color: 'var(--brand-600)', textDecoration: 'none', fontWeight: 600 }}>
            Caja →
          </Link>
        </p>
      )}

      {/* Drawer de cobro · activado desde el CTA del header */}
      {pagoCreditoId && (
        <RegistrarPagoCreditoDrawer
          open={pagoOpen}
          onClose={() => {
            setPagoOpen(false)
            setPagoCreditoId(null)
            onReload()
          }}
          creditoId={pagoCreditoId}
          asociadoNombre={nombreCompleto}
          onSuccess={onReload}
        />
      )}
    </div>
  )
}

// =============================================================
// CUMPLIMIENTO — encapsula todo lo regulatorio
// =============================================================

function CumplimientoTab({ data, onChange }: { data: AsociadoCompleto; onChange: () => void }) {
  const [sub, setSub] = useState<CumplimientoSubtab>('feis')
  const { ultimo_kyc, asociado, feic_extension } = data

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Banner contextual: para qué sirve este tab */}
      <div
        style={{
          display: 'flex', gap: 12, alignItems: 'flex-start',
          padding: '12px 14px',
          background: 'var(--bg)', border: '1px dashed var(--border)',
          borderRadius: 10,
        }}
      >
        <ShieldCheck size={18} weight="duotone" color="var(--n-500)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--text-primary)' }}>Expediente regulatorio del asociado.</strong> Se mantiene actualizado para responder
          requerimientos de IVE / INGECOP. La operación comercial diaria vive
          en los otros tabs — este se consulta en auditoría o cuando piden expediente.
        </div>
      </div>

      {/* Sub-tabs internos */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
        {(
          [
            ['feis', 'FEIS'],
            ...(feic_extension || asociado.requiere_feic ? [['feic', 'FEIC ampliación'] as [CumplimientoSubtab, string]] : []),
            ['kyc', 'KYC'],
            ['documentos', 'Documentos'],
            ['banderas', 'Banderas'],
            ['cuenta_bancaria', 'Cuenta bancaria'],
          ] as Array<[CumplimientoSubtab, string]>
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSub(key)}
            style={{
              all: 'unset', cursor: 'pointer',
              padding: '8px 14px',
              fontSize: 13, fontWeight: sub === key ? 600 : 500,
              color: sub === key ? 'var(--brand-600)' : 'var(--text-muted)',
              borderBottom: sub === key ? '2px solid var(--brand-600)' : '2px solid transparent',
              marginBottom: -1,
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Contenido del sub-tab */}
      <div>
        {sub === 'feis' && <FeisTab data={data} onChange={onChange} />}
        {sub === 'feic' && <FeicTab feic={data.feic} requiereFeic={asociado.requiere_feic} />}
        {sub === 'kyc' && <KycTab kyc={ultimo_kyc} />}
        {sub === 'documentos' && <DocumentosTab kyc={ultimo_kyc} asociadoId={asociado.id} onChanged={onChange} />}
        {sub === 'banderas' && <BanderasInternasTab data={data} onChange={onChange} />}
        {sub === 'cuenta_bancaria' && <CuentaBancariaAsociadoTab data={data} onChange={onChange} />}
      </div>
    </div>
  )
}

// Versión solo-lectura de las banderas internas (para sub-tab)
function BanderasInternasTab({ data, onChange: _onChange }: { data: AsociadoCompleto; onChange: () => void }) {
  const { feis } = data
  if (!feis) return <Empty>El asociado no tiene FEIS capturado.</Empty>
  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 14px', lineHeight: 1.5, maxWidth: 640 }}>
        Banderas internas que disparan la promoción a FEIC ampliado. No se
        incluyen en el JSON oficial IVE — son uso operativo NeoCore.
      </p>
      <div className="ficha-grid">
        <Section title="Banderas regulatorias">
          <KVTable rows={[
            ['PEP', <BoolBadge value={feis.es_pep} />],
            ['CPE', <BoolBadge value={feis.es_cpe} />],
            ['Familiar PEP', <BoolBadge value={feis.es_familiar_pep} />],
          ]} />
        </Section>
      </div>
      <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 16 }}>
        Para editarlas, andá al sub-tab FEIS → tarjeta "Banderas de cumplimiento".
      </p>
    </div>
  )
}

// Sub-tab "Cuenta bancaria" del asociado — referencia bancaria externa
function CuentaBancariaAsociadoTab({ data, onChange }: { data: AsociadoCompleto; onChange: () => void }) {
  const { asociado, referencia_bancaria } = data
  const [editOpen, setEditOpen] = useState(false)

  return (
    <div>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '0 0 18px', lineHeight: 1.5, maxWidth: 640 }}>
        Cuenta bancaria <strong style={{ color: 'var(--text-primary)' }}>externa</strong> del asociado. Se usa para enviarle transferencias
        (retiros, desembolsos de crédito futuros). Por norma INGECOP el titular debe
        coincidir con el asociado.
      </p>

      {!referencia_bancaria ? (
        <div
          style={{
            padding: '40px 24px', background: 'var(--surface)', border: '1px dashed var(--hairline)',
            borderRadius: 12, textAlign: 'center',
          }}
        >
          <Buildings size={28} weight="duotone" color="var(--muted-foreground)" style={{ marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14, color: 'var(--text-primary)' }}>
            No hay cuenta bancaria registrada
          </p>
          <p style={{ margin: '4px 0 16px', fontSize: 12, color: 'var(--text-muted)' }}>
            Sin esta cuenta no se pueden enviar transferencias al asociado.
          </p>
          <button className="btn btn-primary" onClick={() => setEditOpen(true)}>
            Agregar cuenta bancaria
          </button>
        </div>
      ) : (
        <div
          style={{
            background: 'var(--surface)', border: '1px solid var(--hairline)',
            borderRadius: 12, padding: '18px 20px', maxWidth: 480,
            display: 'flex', flexDirection: 'column', gap: 12,
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: 'var(--brand-50)', color: 'var(--brand-600)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Buildings size={18} weight="duotone" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {referencia_bancaria.banco}
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginTop: 2 }}>
                  Cuenta {referencia_bancaria.tipo.toLowerCase()} · {referencia_bancaria.moneda}
                </div>
              </div>
            </div>
            {referencia_bancaria.validada ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: 'var(--ok-ink)', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok-dot)' }} />
                Validada
              </span>
            ) : (
              <span style={{ fontSize: 10, fontWeight: 700, color: '#9a3412', letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Pendiente
              </span>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--hairline)', paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
            <KvSimple label="N° de cuenta" value={referencia_bancaria.numero_cuenta} mono />
            <KvSimple label="Titular" value={referencia_bancaria.titular_nombre} />
            <KvSimple label="Método de validación" value={prettyMetodoValidacion(referencia_bancaria.validacion_metodo)} />
          </div>

          <button
            type="button"
            onClick={() => setEditOpen(true)}
            style={{
              all: 'unset', cursor: 'pointer',
              alignSelf: 'flex-end', marginTop: 4,
              fontSize: 11, fontWeight: 600, color: 'var(--brand-600)',
              padding: '4px 8px', borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', gap: 4,
            }}
          >
            <PencilSimple size={11} weight="bold" />
            Editar
          </button>
        </div>
      )}

      <EditarReferenciaBancariaModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        asociadoId={asociado.id}
        asociadoNombre={asociado.nombre_completo}
        referencia={referencia_bancaria}
        onSuccess={onChange}
      />
    </div>
  )
}

function KvSimple({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <span style={{ color: 'var(--muted-foreground)' }}>{label}</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontFamily: mono ? 'var(--font-mono)' : 'inherit' }}>
        {value}
      </span>
    </div>
  )
}

function prettyMetodoValidacion(m: string | null): string {
  if (!m) return 'Pendiente'
  const map: Record<string, string> = {
    ACH: 'Validada por ACH bancaria',
    KYC_CONFIRMADO: 'Confirmada con DPI físico',
    PENDIENTE: 'Pendiente',
  }
  return map[m] ?? m
}

// Helper: badge contextual que aparece junto al tab Cumplimiento
function cumplimientoBadge(d: AsociadoCompleto): string | null {
  if (d.feic_extension) return 'FEIC'
  if (d.asociado.requiere_feic) return 'FEIC pendiente'
  if (d.ultimo_kyc?.estado === 'review') return 'KYC review'
  if (!d.ultimo_kyc) return 'KYC'
  return null
}

// Humanización del perfil económico declarado en el onboarding de la app.
// Ojo: hoy el submit del app guarda estos valores reales en
// kyc.datos_extraidos.app_extras (el FEIS queda con el default 'SALARIO' /
// ingresos NULL). Por eso el resumen lee de app_extras y cae al FEIS.
const ORIGEN_FONDOS_LABEL: Record<string, string> = {
  SALARIO: 'Salario',
  NEGOCIO_PROPIO: 'Negocio propio',
  PROFESION_INDEPENDIENTE: 'Profesión independiente',
  REMESAS: 'Remesas',
  INVERSIONES: 'Inversiones',
  PENSION: 'Pensión',
  OTRO: 'Otro',
}
const INGRESO_RANGO_LABEL: Record<string, string> = {
  '<2500': 'Menos de Q2,500',
  '2500_5000': 'Q2,500 – Q5,000',
  '5000_10000': 'Q5,000 – Q10,000',
  '10000_25000': 'Q10,000 – Q25,000',
  '25000_50000': 'Q25,000 – Q50,000',
  '>50000': 'Más de Q50,000',
}
const CONTACTO_PREF_LABEL: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  LLAMADA: 'Llamada',
  SMS: 'SMS',
  EMAIL: 'Email',
  CORREO: 'Email',
}

function FeisTab({ data, onChange }: { data: AsociadoCompleto; onChange: () => void }) {
  const { feis, asociado, ultimo_kyc, feis_actividad, feis_productos, feic_extension, feic_promociones } = data
  const appExtras =
    (ultimo_kyc?.datos_extraidos as { app_extras?: Record<string, unknown> } | null | undefined)
      ?.app_extras ?? {}
  const ecoOrigen = (appExtras.origen_fondos as string) ?? feis?.origen_fondos ?? null
  const ecoIngresoCode = (appExtras.ingresos_mensuales_aprox as string) ?? null
  const ecoContacto = (appExtras.forma_preferida_contacto as string) ?? null
  const ecoIngresoTexto = ecoIngresoCode
    ? (INGRESO_RANGO_LABEL[ecoIngresoCode] ?? ecoIngresoCode)
    : feis?.ingresos_mensuales_aprox != null
      ? `Q ${Number(feis.ingresos_mensuales_aprox).toLocaleString('es-GT')}`
      : '—'
  const [editar, setEditar] = useState<FeisSeccion | null>(null)
  const tieneFeic = !!feic_extension
  const ultimaPromocion = (feic_promociones ?? [])[0]

  if (!feis) return <Empty>El asociado no tiene FEIS capturado.</Empty>

  return (
    <>
      {/* Action bar — meta + descarga regulatoria */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            className="badge"
            style={{
              background: tieneFeic ? 'var(--brand-50)' : 'var(--n-100)',
              color: tieneFeic ? 'var(--brand-700)' : 'var(--n-700)',
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              letterSpacing: 0.5,
            }}
          >
            {tieneFeic ? 'FEIC AMPLIADO' : 'FEIS'}
          </span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            v{feis.version} · capturado {fmtFecha(feis.fecha_captura)}
          </span>
        </div>
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => descargarExpedienteIveJson(data)}
          style={{ fontSize: 12 }}
        >
          Descargar para IVE (JSON)
        </button>
      </div>

      {/* Banner FEIC — si fue promovido se ve cuándo y por qué */}
      {tieneFeic && ultimaPromocion && (
        <div
          style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '12px 14px', marginBottom: 18,
            background: 'var(--brand-50)', border: '1px solid var(--brand-100, var(--brand-200))',
            borderRadius: 10, lineHeight: 1.5,
          }}
        >
          <ShieldCheck size={18} weight="duotone" color="var(--brand-600)" style={{ flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              Expediente FEIC ampliado
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              Promovido el {fmtFecha(ultimaPromocion.promovido_at)} · evento: {prettyEvento(ultimaPromocion.evento_disparador)}
              {ultimaPromocion.motivo && <> · {ultimaPromocion.motivo}</>}
            </div>
          </div>
        </div>
      )}

      {/* ========== 1. INFORMACIÓN GENERAL (portal IVE) ========== */}
      <SeccionPortal numero={1} titulo="Información General" subtitulo="Datos personales, residencia y contacto del asociado.">
        <div className="ficha-grid">
          <Section title="Identificación" onEdit={() => setEditar('identificacion')}>
            <KVTable rows={[
              ['Tipo documento', feis.tipo_documento],
              ['Número', feis.numero_documento],
              ['Vigencia', fmtFecha(feis.vigencia_documento)],
              ['Nacionalidad', feis.nacionalidad],
            ]} />
          </Section>
          <Section title="Datos personales" onEdit={() => setEditar('datos_personales')}>
            <KVTable rows={[
              ['Primer nombre', feis.primer_nombre],
              ['Segundo nombre', feis.segundo_nombre ?? '—'],
              ['Otros nombres', feis.otros_nombres ?? '—'],
              ['Primer apellido', feis.primer_apellido],
              ['Segundo apellido', feis.segundo_apellido ?? '—'],
              ['Apellido casada', feis.apellido_casada ?? '—'],
              ['Sexo', capitalize(feis.genero)],
              ['Fecha nacimiento', fmtFecha(feis.fecha_nacimiento)],
              ['Estado civil', capitalize(feis.estado_civil ?? '—')],
              ['País de nacimiento', feis.pais_nacimiento ?? '—'],
              ['Departamento de nacimiento', feis.departamento_nacimiento ?? '—'],
              ['Municipio de nacimiento', feis.municipio_nacimiento ?? '—'],
            ]} />
          </Section>
          <Section title="Domicilio" onEdit={() => setEditar('domicilio')}>
            <KVTable rows={[
              ['Dirección', feis.direccion_completa],
              ['Zona', feis.zona ?? '—'],
              ['Colonia', feis.colonia ?? '—'],
              ['Municipio', feis.municipio],
              ['Departamento', feis.departamento],
              ['País', feis.pais_residencia],
              ['Teléfono', feis.telefono ?? '—'],
            ]} />
          </Section>
        </div>
      </SeccionPortal>

      {/* ========== 2. INFORMACIÓN ECONÓMICA ========== */}
      <SeccionPortal
        numero={2}
        titulo="Información Económica"
        subtitulo="Perfil económico declarado por el asociado y su evolución."
      >
        <div className="ficha-grid">
          <Section title="Perfil económico declarado">
            <KVTable rows={[
              ['Origen de fondos', ecoOrigen ? (ORIGEN_FONDOS_LABEL[ecoOrigen] ?? ecoOrigen) : '—'],
              ['Ingreso mensual aprox.', ecoIngresoTexto],
              ['Profesión / oficio', (feis.profesion_oficio ?? '').trim() || '—'],
              ['Actividad económica', feis.actividad_economica ?? '—'],
              ['Contacto preferido', ecoContacto ? (CONTACTO_PREF_LABEL[ecoContacto] ?? ecoContacto) : '—'],
            ]} />
          </Section>
        </div>

        {/* Timeline de actualizaciones (solo si hay historial cargado) */}
        {(feis_actividad ?? []).length > 0 && (
          <div style={{ marginTop: 16 }}>
            <ActividadEconomicaTimeline lista={feis_actividad ?? []} />
          </div>
        )}
      </SeccionPortal>

      {/* ========== 3. PRODUCTOS Y SERVICIOS ========== */}
      <SeccionPortal
        numero={3}
        titulo="Productos y Servicios"
        subtitulo="Cuentas, créditos y plazos que el asociado tiene contratados con la cooperativa."
      >
        <ProductosFeisGrid
          lista={feis_productos ?? []}
          ctaApo={data.cuenta_aportaciones}
          ctasAho={data.cuentas_ahorro ?? []}
        />
      </SeccionPortal>

      {/* ========== Banderas internas de cumplimiento (NO van al JSON IVE) ========== */}
      <div
        style={{
          marginTop: 24, padding: '14px 16px',
          background: 'var(--surface)', border: '1px dashed var(--border)',
          borderRadius: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
          <div>
            <h3 style={{ fontSize: 12, fontWeight: 700, margin: 0, color: 'var(--n-700)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Banderas de cumplimiento · uso interno NeoCore
            </h3>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '2px 0 0', lineHeight: 1.5 }}>
              Disparadores que pueden requerir promoción a FEIC. No se incluyen en el JSON oficial IVE.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setEditar('banderas')}
            style={{
              all: 'unset', cursor: 'pointer',
              fontSize: 11, fontWeight: 600, color: 'var(--brand-600)',
              padding: '2px 6px', borderRadius: 4,
            }}
          >
            Editar
          </button>
        </div>
        <KVTable rows={[
          ['PEP', <BoolBadge value={feis.es_pep} />],
          ['CPE', <BoolBadge value={feis.es_cpe} />],
          ['Familiar PEP', <BoolBadge value={feis.es_familiar_pep} />],
        ]} />
      </div>

      {/* ========== Auditoría operativa (footer) ========== */}
      <div
        style={{
          marginTop: 16, padding: '14px 16px',
          background: 'var(--bg)', border: '1px solid var(--border)',
          borderRadius: 10, fontSize: 12, color: 'var(--text-muted)',
          display: 'flex', flexWrap: 'wrap', gap: 24,
        }}
      >
        <span>Versión: <strong>v{feis.version}</strong></span>
        <span>Capturado: {fmtFechaHora(feis.fecha_captura)}</span>
        <span>Canal: {feis.canal_captura ?? '—'}</span>
        <span>Verificado: {fmtFechaHora(feis.verificado_at)}</span>
      </div>

      <EditarFeisSeccionModal
        open={editar !== null}
        onClose={() => setEditar(null)}
        asociadoId={asociado.id}
        seccion={editar}
        feis={feis}
        onSuccess={onChange}
      />
    </>
  )
}

// ----- Sección con número grande estilo portal IVE -----
function SeccionPortal({
  numero, titulo, subtitulo, children,
}: {
  numero: number
  titulo: string
  subtitulo?: string
  children: ReactNode
}) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
        <div
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: 'var(--brand-50)', color: 'var(--brand-600)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, fontWeight: 700, flexShrink: 0,
          }}
        >
          {numero}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: 16, margin: 0, fontWeight: 700, color: 'var(--text-primary)' }}>
            {titulo}
          </h2>
          {subtitulo && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0', lineHeight: 1.5 }}>
              {subtitulo}
            </p>
          )}
        </div>
      </div>
      <div>{children}</div>
    </div>
  )
}

// ----- Timeline económico (Información Económica del portal IVE) -----
function ActividadEconomicaTimeline({ lista }: { lista: AsociadoCompleto['feis_actividad'] }) {
  if (!lista || lista.length === 0) {
    return (
      <Empty>
        Aún no se ha capturado información económica del asociado. La actividad
        económica inicial se registra al momento del alta.
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" disabled style={{ fontSize: 13 }}>
            Capturar información inicial
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Próximamente</p>
        </div>
      </Empty>
    )
  }

  // Ordenamos: INICIAL primero, después ACTUALIZACIONES por número de orden
  const ordenadas = [...lista].sort((a, b) => {
    if (a.tipo === 'INICIAL') return -1
    if (b.tipo === 'INICIAL') return 1
    return a.numero_orden - b.numero_orden
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {ordenadas.map((act) => (
        <div
          key={act.id}
          className="card"
          style={{ display: 'flex', gap: 14, padding: '14px 16px', alignItems: 'flex-start' }}
        >
          <div
            style={{
              padding: '4px 10px', borderRadius: 6, fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
              background: act.tipo === 'INICIAL' ? 'var(--brand-50)' : 'var(--n-100)',
              color: act.tipo === 'INICIAL' ? 'var(--brand-700)' : 'var(--n-700)',
              flexShrink: 0,
            }}
          >
            {act.tipo === 'INICIAL' ? 'INICIAL' : `ACT. ${act.numero_orden}`}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 14, margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>
              {fmtMoneda(act.monto_ingresos)} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>/ mes aprox.</span>
            </p>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0', lineHeight: 1.5 }}>
              {act.origen_ingresos_texto}
            </p>
            <p style={{ fontSize: 11, color: 'var(--n-400)', margin: '6px 0 0' }}>
              Capturado el {fmtFecha(act.fecha)} · {fmtRelativo(act.capturado_at)}
            </p>
          </div>
        </div>
      ))}
      <div style={{ marginTop: 4 }}>
        <button className="btn btn-ghost" disabled style={{ fontSize: 12 }}>
          Agregar actualización
        </button>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>Próximamente</span>
      </div>
    </div>
  )
}

// ----- Productos del asociado estilo cards Mercury -----
function ProductosFeisGrid({
  lista, ctaApo, ctasAho,
}: {
  lista: AsociadoCompleto['feis_productos']
  ctaApo: AsociadoCompleto['cuenta_aportaciones']
  ctasAho: AsociadoCompleto['cuentas_ahorro']
}) {
  if (!lista || lista.length === 0) {
    return (
      <Empty>
        El asociado todavía no tiene productos registrados. Las cuentas se
        crean automáticamente al registrar el pago de inscripción en Caja
        (cuenta de aportaciones + cuenta de ahorro corriente).
      </Empty>
    )
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
      {lista.map((p) => (
        <ProductoCard key={p.id} p={p} saldoReal={saldoRealDeProducto(p, ctaApo, ctasAho)} />
      ))}
    </div>
  )
}

function ProductoCard({
  p, saldoReal,
}: {
  p: AsociadoCompleto['feis_productos'][number]
  /**
   * Saldo real desde la cuenta correspondiente (cuenta_aportaciones /
   * cuenta_ahorro_corriente). Cuando viene se prioriza sobre p.valor
   * que es el snapshot regulatorio del FEIS. null cuando no hay match
   * (ej. PLAZO_FIJO o CREDITO antes de tener su módulo).
   */
  saldoReal?: number | null
}) {
  const totalBenef = (p.beneficiarios ?? []).reduce((s, b) => s + Number(b.porcentaje), 0)
  const saldoMostrar = saldoReal ?? Number(p.valor)
  // Si tenemos saldo real y difiere del snapshot regulatorio, mostramos hint
  const desfasado = saldoReal != null && Math.abs(saldoReal - Number(p.valor)) > 0.01

  return (
    <div
      style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {prettyTipoProducto(p.tipo_producto)}
        </span>
        <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: p.estado === 'vigente' ? 'var(--brand-50)' : 'var(--n-100)', color: p.estado === 'vigente' ? 'var(--brand-700)' : 'var(--n-600)' }}>
          {p.estado}
        </span>
      </div>
      <div>
        <p style={{ fontSize: 22, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
          {p.moneda} {fmtMoneda(saldoMostrar)?.replace(/^Q\s?/, '')}
        </p>
        <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
          {p.nombre_descripcion}
        </p>
        {desfasado && (
          <p
            style={{ fontSize: 10, color: 'var(--text-muted)', margin: '4px 0 0', lineHeight: 1.4 }}
            title="El FEIS regulatorio guarda Q{p.valor}. El saldo real difiere por movimientos posteriores que aún no se sincronizaron al expediente."
          >
            FEIS: {p.moneda} {fmtMoneda(Number(p.valor))?.replace(/^Q\s?/, '')} (snapshot regulatorio, pendiente sync)
          </p>
        )}
      </div>
      {p.identificador && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-mono)' }}>
          № {p.identificador}
        </p>
      )}
      <div style={{ display: 'flex', gap: 16, fontSize: 11, color: 'var(--text-muted)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <span><strong style={{ color: 'var(--text-primary)' }}>{(p.firmantes ?? []).length}</strong> firmante{(p.firmantes ?? []).length === 1 ? '' : 's'}</span>
        <span><strong style={{ color: 'var(--text-primary)' }}>{(p.tarjetahabientes ?? []).length}</strong> tarjeta{(p.tarjetahabientes ?? []).length === 1 ? '' : 's'}</span>
        <span><strong style={{ color: 'var(--text-primary)' }}>{(p.beneficiarios ?? []).length}</strong> benef. ({totalBenef}%)</span>
      </div>
    </div>
  )
}

/**
 * Devuelve el saldo real de la cuenta que respalda este producto del FEIS.
 * En v0 la relación es 1:1 por tipo (un asociado tiene una cuenta de
 * aportaciones y una de ahorro corriente). Cuando el módulo tenga múltiples
 * cuentas del mismo tipo, refinar matcheando por p.identificador.
 */
function saldoRealDeProducto(
  p: AsociadoCompleto['feis_productos'][number],
  ctaApo: AsociadoCompleto['cuenta_aportaciones'],
  ctasAho: AsociadoCompleto['cuentas_ahorro'],
): number | null {
  if (p.tipo_producto === 'APORTACIONES') {
    return ctaApo ? Number(ctaApo.saldo) : null
  }
  if (p.tipo_producto === 'AHORRO_CORRIENTE') {
    // 1:1 en v0; si en el futuro hay varias cuentas, matchear por identificador
    const cta = ctasAho[0]
    return cta ? Number(cta.saldo) : null
  }
  // PLAZO_FIJO y CREDITO no tienen cuenta real todavía → null = usar p.valor
  return null
}

// Mapeos para legibilidad
function prettyTipoProducto(t: string): string {
  const map: Record<string, string> = {
    AHORRO_CORRIENTE: 'Ahorro corriente',
    APORTACIONES: 'Aportaciones',
    PLAZO_FIJO: 'Plazo fijo',
    CREDITO: 'Crédito',
  }
  return map[t] ?? t
}

// Pasivos = captación: la coop le DEBE al asociado (le tiene su dinero)
const TIPOS_PASIVOS = new Set(['AHORRO_CORRIENTE', 'APORTACIONES', 'PLAZO_FIJO'])
// Activos = cartera: el asociado le DEBE a la coop (recibió un crédito)
const TIPOS_ACTIVOS = new Set(['CREDITO'])

function esPasivo(t: string): boolean { return TIPOS_PASIVOS.has(t) }
function esActivo(t: string): boolean { return TIPOS_ACTIVOS.has(t) }

function agruparPorTipo(productos: AsociadoCompleto['feis_productos']): Array<[string, number]> {
  const map = new Map<string, number>()
  for (const p of productos) {
    map.set(p.tipo_producto, (map.get(p.tipo_producto) ?? 0) + Number(p.valor))
  }
  return Array.from(map.entries()).sort(([, a], [, b]) => b - a)
}
void agruparPorTipo

// =============================================================
// HeroCard — card de saldo del Resumen, con isotipo Neocoop sutil
// =============================================================
// 4 variantes:
//   brand-solid   → gradient brand naranja sólido + isotipo blanco al fondo
//   brand-soft    → fondo cream + borde brand suave + texto brand-700 (estado vacío)
//   brand-outline → blanco con borde brand fuerte + acento brand (segundo plano)
//   neutral-soft  → blanco con borde dashed (sin saldo, neutral)
// =============================================================
type HeroVariant = 'brand-solid' | 'brand-soft' | 'brand-outline' | 'neutral-soft'

function HeroCard({
  variant, eyebrow, monto, subtitulo, children,
}: {
  variant: HeroVariant
  eyebrow: string
  monto: string
  subtitulo: string
  children?: ReactNode
}) {
  const styles = (() => {
    switch (variant) {
      case 'brand-solid':
        return {
          background: 'linear-gradient(135deg, var(--brand-600) 0%, var(--brand-deep) 100%)',
          color: '#fff',
          border: 'none',
          boxShadow: '0 10px 28px rgba(255,91,4,0.18)',
          eyebrowOpacity: 0.88,
          subOpacity: 0.85,
          isotipo: '/brand/isotipo-blanco.svg',
          isotipoOpacity: 0.10,
        }
      case 'brand-soft':
        return {
          background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff5ee 100%)',
          color: 'var(--brand-700)',
          border: '1px solid var(--brand-100, var(--brand-200))',
          boxShadow: 'none',
          eyebrowOpacity: 0.85,
          subOpacity: 0.75,
          isotipo: '/brand/isotipo-naranja.svg',
          isotipoOpacity: 0.08,
        }
      case 'brand-outline':
        return {
          background: 'var(--surface)',
          color: 'var(--text-primary)',
          border: '1.5px solid var(--brand-600)',
          boxShadow: '0 6px 20px rgba(255,91,4,0.06)',
          eyebrowOpacity: 1,
          subOpacity: 1,
          isotipo: '/brand/isotipo-naranja.svg',
          isotipoOpacity: 0.06,
        }
      case 'neutral-soft':
        return {
          background: 'var(--surface)',
          color: 'var(--text-secondary)',
          border: '1px dashed var(--border)',
          boxShadow: 'none',
          eyebrowOpacity: 0.7,
          subOpacity: 0.65,
          isotipo: null,
          isotipoOpacity: 0,
        }
    }
  })()

  return (
    <div
      style={{
        position: 'relative',
        background: styles.background,
        color: styles.color,
        border: styles.border,
        borderRadius: 16,
        padding: '20px 22px',
        boxShadow: styles.boxShadow,
        overflow: 'hidden',
      }}
    >
      {/* Isotipo decorativo en esquina inferior derecha */}
      {styles.isotipo && (
        <img
          src={styles.isotipo}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            right: -18, bottom: -22,
            width: 130, height: 130,
            opacity: styles.isotipoOpacity,
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      )}

      <div style={{ position: 'relative', zIndex: 1 }}>
        <p
          style={{
            fontSize: 11, fontWeight: 700, margin: 0,
            opacity: styles.eyebrowOpacity, letterSpacing: 0.6, textTransform: 'uppercase',
            color: variant === 'brand-outline' ? 'var(--brand-600)' : (variant === 'brand-soft' ? 'var(--brand-700)' : 'inherit'),
          }}
        >
          {eyebrow}
        </p>
        <p
          style={{
            fontSize: 32, fontWeight: 700, margin: '4px 0 0',
            letterSpacing: -0.5, fontFeatureSettings: '"tnum"',
            color: variant === 'brand-outline' ? 'var(--brand-700)' : 'inherit',
          }}
        >
          {monto}
        </p>
        <p style={{ fontSize: 11, margin: '4px 0 0', opacity: styles.subOpacity, lineHeight: 1.5 }}>
          {subtitulo}
        </p>
        {children}
      </div>

      {/* Borde-acento brand del lado izquierdo (solo brand-outline) */}
      {variant === 'brand-outline' && (
        <div
          style={{
            position: 'absolute', left: 0, top: 0, bottom: 0, width: 4,
            background: 'var(--brand-600)',
            borderTopLeftRadius: 16, borderBottomLeftRadius: 16,
          }}
        />
      )}
    </div>
  )
}
// HeroCard / SaldosBars son los componentes legacy del hero anterior. Los
// dejamos disponibles para reuso en otras vistas (cumplimiento, históricos,
// etc.) pero el Resumen ya no los referencia — usa PosicionFinancieraHero.
void HeroCard

// Mini-barras dentro del hero para desglosar por tipo de producto
// =============================================================
// ContactoRapidoChips — chips modernos con icons, hover sutil, sin
// "card" genérica. Cada chip es un link real si hay canal disponible,
// o queda atenuado si no.
// =============================================================
function ContactoRapidoChips({ wa, email }: { wa: string | null; email: string | null }) {
  const channels: Array<{ key: string; label: string; href: string | null; Icon: PhosphorIcon }> = [
    { key: 'wa',   label: 'WhatsApp', href: wa ? `https://wa.me/${wa.replace(/\D/g, '')}` : null, Icon: Phone },
    { key: 'call', label: 'Llamar',   href: wa ? `tel:${wa.replace(/\D/g, '')}` : null,           Icon: Phone },
    { key: 'mail', label: 'Email',    href: email ? `mailto:${email}` : null,                     Icon: Phone },
  ]
  const hasAny = channels.some((c) => c.href)
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{
        fontSize: 10, fontWeight: 800, letterSpacing: 2, textTransform: 'uppercase',
        color: 'var(--muted-foreground)',
      }}>
        Contactar
      </span>
      {channels.map((c) => {
        const disabled = !c.href
        return (
          <a
            key={c.key}
            href={c.href ?? undefined}
            target={c.key === 'wa' ? '_blank' : undefined}
            rel={c.key === 'wa' ? 'noopener noreferrer' : undefined}
            onClick={(e) => { if (disabled) e.preventDefault() }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 12px', borderRadius: 99,
              background: disabled ? 'var(--n-100)' : 'var(--surface)',
              color: disabled ? 'var(--muted-foreground)' : 'var(--ink)',
              border: '1px solid var(--hairline)',
              fontSize: 12, fontWeight: 600,
              textDecoration: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.55 : 1,
              transition: 'transform 0.12s ease, border-color 0.12s ease',
            }}
            onMouseEnter={(e) => {
              if (disabled) return
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--brand-300)'
              ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.borderColor = 'var(--hairline)'
              ;(e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)'
            }}
          >
            <c.Icon size={12} weight="bold" />
            {c.label}
          </a>
        )
      })}
      {!hasAny && (
        <span style={{ fontSize: 11, color: 'var(--muted-foreground)', marginLeft: 4 }}>
          Sin canales registrados — agregalos en Contactabilidad.
        </span>
      )}
    </div>
  )
}

// =============================================================
// CreditosResumenSeccion — visibilidad accionable de la cartera
// directamente en el Resumen. Cards moderna por crédito con:
//   - Tipo + badge de estado
//   - Saldo grande tabular
//   - Próxima cuota con badge "vence en X días / hoy / mañana"
//   - CTA "Ver detalle" → /neocore/creditos/:id
// =============================================================
function CreditosResumenSeccion({ asociadoId }: { asociadoId: string }) {
  type CreditoCard = {
    id: string
    tipoNombre: string
    saldoCapital: number
    estado: string
    diasParaProx: number | null
    proximaCuota: number
    proximoVence: string | null
    tieneMora: boolean
  }
  const [list, setList] = useState<CreditoCard[]>([])
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    void (async () => {
      const { data } = await supabase
        .from('credito')
        .select(`
          id, saldo_capital, estado,
          tipo:tipo_credito_id (nombre),
          cuotas:credito_cuota (
            numero, fecha_vencimiento, capital, interes, mora_acumulada,
            capital_pagado, interes_pagado, mora_pagada, estado, dias_atraso_actual
          )
        `)
        .eq('asociado_id', asociadoId)
        .in('estado', ['vigente', 'en_mora', 'al_dia'])
        .order('created_at', { ascending: false })

      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const rows: CreditoCard[] = (data ?? []).map((r) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const c = r as any
        const cuotasPend = (c.cuotas ?? []).filter(
          (q: { estado: string }) => q.estado !== 'pagada' && q.estado !== 'castigada',
        ).sort((a: { numero: number }, b: { numero: number }) => a.numero - b.numero)
        const prox = cuotasPend[0]
        let diasParaProx: number | null = null
        if (prox) {
          const v = new Date(String(prox.fecha_vencimiento).slice(0, 10) + 'T00:00:00')
          diasParaProx = Math.round((v.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
        }
        const proxMonto = prox
          ? (Number(prox.capital) - Number(prox.capital_pagado)) +
            (Number(prox.interes) - Number(prox.interes_pagado)) +
            (Number(prox.mora_acumulada) - Number(prox.mora_pagada))
          : 0
        const tieneMora = cuotasPend.some(
          (q: { dias_atraso_actual: number }) => Number(q.dias_atraso_actual) > 0,
        )
        return {
          id: c.id,
          tipoNombre: c.tipo?.nombre ?? 'Crédito',
          saldoCapital: Number(c.saldo_capital),
          estado: c.estado,
          diasParaProx,
          proximaCuota: proxMonto,
          proximoVence: prox?.fecha_vencimiento ?? null,
          tieneMora,
        }
      })
      setList(rows)
      setLoaded(true)
    })()
  }, [asociadoId])

  if (!loaded || list.length === 0) return null

  return (
    <div>
      <h3 style={{
        margin: '0 0 12px', fontSize: 10, fontWeight: 800,
        letterSpacing: 2, color: 'var(--brand-700)', textTransform: 'uppercase',
      }}>
        Cartera activa · {list.length} crédito{list.length === 1 ? '' : 's'}
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
        gap: 12,
      }}>
        {list.map((c) => <CreditoCardResumen key={c.id} c={c} />)}
      </div>
    </div>
  )
}

function CreditoCardResumen({
  c,
}: {
  c: {
    id: string
    tipoNombre: string
    saldoCapital: number
    estado: string
    diasParaProx: number | null
    proximaCuota: number
    proximoVence: string | null
    tieneMora: boolean
  }
}) {
  // Tono del badge de vencimiento: rojo si mora, ámbar si <= 3 días, brand si > 3
  const proxBadge = (() => {
    if (c.tieneMora) {
      return { label: 'En mora', bg: 'rgba(163,45,45,0.10)', color: 'var(--danger, #a32d2d)' }
    }
    if (c.diasParaProx === null) return null
    if (c.diasParaProx < 0) return { label: `${Math.abs(c.diasParaProx)} días vencido`, bg: 'rgba(163,45,45,0.10)', color: 'var(--danger, #a32d2d)' }
    if (c.diasParaProx === 0) return { label: 'Vence hoy', bg: 'rgba(217,119,6,0.12)', color: 'var(--warning, #d97706)' }
    if (c.diasParaProx === 1) return { label: 'Vence mañana', bg: 'rgba(217,119,6,0.12)', color: 'var(--warning, #d97706)' }
    if (c.diasParaProx <= 3) return { label: `Vence en ${c.diasParaProx} días`, bg: 'rgba(217,119,6,0.10)', color: 'var(--warning, #d97706)' }
    if (c.diasParaProx <= 7) return { label: `Vence en ${c.diasParaProx} días`, bg: 'var(--brand-50)', color: 'var(--brand-700)' }
    return { label: `Vence en ${c.diasParaProx} días`, bg: 'var(--soft-gray)', color: 'var(--ink-soft)' }
  })()

  return (
    <Link
      to={`/neocore/creditos/${c.id}`}
      style={{
        textDecoration: 'none', color: 'inherit',
        borderRadius: 16, overflow: 'hidden',
        background: 'var(--surface)', border: '1px solid var(--hairline)',
        boxShadow: 'var(--shadow-sm)',
        display: 'flex', flexDirection: 'column',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-2px)'
        ;(e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-md)'
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(0)'
        ;(e.currentTarget as HTMLAnchorElement).style.boxShadow = 'var(--shadow-sm)'
      }}
    >
      {/* Body principal */}
      <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span style={{
            fontSize: 10, fontWeight: 800, color: 'var(--muted-foreground)',
            textTransform: 'uppercase', letterSpacing: 1.5,
          }}>
            {c.tipoNombre}
          </span>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            fontSize: 9, fontWeight: 800, color: 'var(--ok-ink)',
            textTransform: 'uppercase', letterSpacing: 0.8,
          }}>
            ● Vigente
          </span>
        </div>
        <div>
          <p style={{
            margin: 0, fontSize: 10, color: 'var(--muted-foreground)',
            textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1,
          }}>
            Saldo capital
          </p>
          <p style={{
            margin: '2px 0 0', fontSize: 26, fontWeight: 800,
            color: 'var(--ink)', fontFeatureSettings: '"tnum"', letterSpacing: '-0.01em',
          }}>
            {fmtMoneda(c.saldoCapital)}
          </p>
        </div>
      </div>

      {/* Footer con próxima cuota destacada */}
      {c.proximaCuota > 0 && (
        <div style={{
          padding: '12px 20px',
          background: 'var(--bg)',
          borderTop: '1px dashed var(--hairline)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div>
            <p style={{
              margin: 0, fontSize: 9, color: 'var(--muted-foreground)',
              textTransform: 'uppercase', fontWeight: 700, letterSpacing: 1,
            }}>
              Próxima cuota
            </p>
            <p style={{
              margin: '1px 0 0', fontSize: 16, fontWeight: 800,
              color: 'var(--ink)', fontFeatureSettings: '"tnum"',
            }}>
              {fmtMoneda(c.proximaCuota)}
            </p>
          </div>
          {proxBadge && (
            <span style={{
              padding: '6px 10px', borderRadius: 99,
              background: proxBadge.bg, color: proxBadge.color,
              fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
              letterSpacing: 0.5, whiteSpace: 'nowrap',
            }}>
              {proxBadge.label}
            </span>
          )}
        </div>
      )}
    </Link>
  )
}

// =============================================================
// PosicionFinancieraHero — rediseño 2026 del bloque de saldos.
// En lugar de dos cards genéricas con barras horizontales (look
// dashboard 2010), planteamos:
//   - Hero con la POSICIÓN NETA del asociado (la métrica que importa).
//   - Grid de dos columnas: Captación (lo que tiene) + Cartera (lo
//     que debe). Cada lado es un sub-bloque con su jerarquía propia.
//   - Líneas de producto con visual de chip + barra fina, tipografía
//     tabular para los montos, micro-meta debajo.
//   - Layout que respira y aprovecha la paleta brand sin saturar.
// =============================================================
function PosicionFinancieraHero({
  primerNombre, totalPasivos, totalActivos,
  ctaApoSaldo, totalAhorro, totalPF, creditos,
  sinProductos, fechaIngreso,
}: {
  primerNombre: string
  totalPasivos: number
  totalActivos: number
  ctaApoSaldo: number
  totalAhorro: number
  totalPF: number
  creditos: Array<{ tipo: string; saldo: number; proximaCuota: number; proximoVence: string | null }>
  sinProductos: boolean
  fechaIngreso: string | null
}) {
  const neto = totalPasivos - totalActivos
  const direccion: 'favor' | 'contra' | 'cero' =
    neto > 0 ? 'favor' : neto < 0 ? 'contra' : 'cero'

  // Empty state cuando no hay productos todavía
  if (sinProductos && creditos.length === 0) {
    return (
      <div
        style={{
          padding: '32px 36px', borderRadius: 22,
          background: 'linear-gradient(135deg, var(--brand-50) 0%, #fff 100%)',
          border: '1px solid var(--hairline)',
          boxShadow: 'var(--shadow-sm)',
          textAlign: 'center',
        }}
      >
        <p style={{
          margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
          textTransform: 'uppercase', color: 'var(--brand-700)',
        }}>
          Sin productos todavía
        </p>
        <p style={{ margin: '8px 0 0', fontSize: 16, color: 'var(--ink-soft)', maxWidth: 520, marginInline: 'auto', lineHeight: 1.5 }}>
          Cuando registremos el pago de inscripción, se abrirán automáticamente
          la cuenta de aportaciones y la de ahorro corriente.
        </p>
      </div>
    )
  }

  const pasivosBreakdown: Array<{ label: string; monto: number; tono: 'apo' | 'ahorro' | 'pf' }> = []
  if (totalPF > 0) pasivosBreakdown.push({ label: 'Plazo fijo', monto: totalPF, tono: 'pf' })
  if (totalAhorro > 0) pasivosBreakdown.push({ label: 'Ahorro corriente', monto: totalAhorro, tono: 'ahorro' })
  if (ctaApoSaldo > 0) pasivosBreakdown.push({ label: 'Aportaciones', monto: ctaApoSaldo, tono: 'apo' })

  return (
    <div
      style={{
        position: 'relative',
        padding: '28px 32px',
        borderRadius: 22,
        background: 'linear-gradient(135deg, #fff 0%, var(--brand-50) 100%)',
        border: '1px solid var(--hairline)',
        boxShadow: 'var(--shadow-md)',
        overflow: 'hidden',
      }}
    >
      {/* Acento decorativo sutil */}
      <div style={{
        position: 'absolute', top: -80, right: -80, width: 240, height: 240,
        borderRadius: '50%', background: 'var(--brand-100)', opacity: 0.5,
        filter: 'blur(40px)', pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* HERO — posición neta */}
        <div>
          <p style={{
            margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
            color: 'var(--brand-700)', textTransform: 'uppercase',
          }}>
            Posición financiera
          </p>
          <p style={{
            margin: '8px 0 0', fontSize: 44, fontWeight: 800, lineHeight: 1,
            letterSpacing: '-0.02em', color: 'var(--ink)',
            fontFeatureSettings: '"tnum"',
          }}>
            {fmtMoneda(Math.abs(neto))}
          </p>
          <p style={{
            margin: '8px 0 0', fontSize: 13, color: 'var(--ink-soft)',
          }}>
            {direccion === 'favor' && (
              <>
                <strong style={{ color: 'var(--ok-ink)' }}>a favor de {primerNombre}</strong>
                {fechaIngreso && <> · asociado desde {fmtFecha(fechaIngreso)}</>}
              </>
            )}
            {direccion === 'contra' && (
              <strong style={{ color: 'var(--warning, #d97706)' }}>
                a favor de la cooperativa
              </strong>
            )}
            {direccion === 'cero' && 'Balance neto en cero'}
          </p>
        </div>

        {/* Grid: Captación + Cartera */}
        <div style={{
          display: 'grid', gap: 14,
          gridTemplateColumns: totalActivos > 0 ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr',
        }}>
          {/* ── Captación ── */}
          {totalPasivos > 0 && (
            <BloqueCaptacionCartera
              tono="captacion"
              eyebrow="Captación"
              total={totalPasivos}
              subtitulo={`${pasivosBreakdown.length} producto${pasivosBreakdown.length === 1 ? '' : 's'} de captación`}
              items={pasivosBreakdown.map((p) => ({
                label: p.label,
                monto: p.monto,
                pct: (p.monto / totalPasivos) * 100,
                tono: p.tono,
              }))}
            />
          )}

          {/* ── Cartera (solo si hay créditos) ── */}
          {totalActivos > 0 && (
            <BloqueCaptacionCartera
              tono="cartera"
              eyebrow="Cartera"
              total={totalActivos}
              subtitulo={
                creditos.length === 1 && creditos[0].proximaCuota > 0
                  ? `Próx. ${fmtMoneda(creditos[0].proximaCuota)} · vence ${creditos[0].proximoVence ? fmtFecha(creditos[0].proximoVence) : ''}`
                  : `${creditos.length} crédito${creditos.length === 1 ? '' : 's'} vigente${creditos.length === 1 ? '' : 's'}`
              }
              items={creditos.map((c) => ({
                label: c.tipo,
                monto: c.saldo,
                pct: (c.saldo / totalActivos) * 100,
                tono: 'credito' as const,
                hint: c.proximoVence ? `Vence ${fmtFecha(c.proximoVence)}` : undefined,
              }))}
            />
          )}
        </div>
      </div>
    </div>
  )
}

function BloqueCaptacionCartera({
  tono, eyebrow, total, subtitulo, items,
}: {
  tono: 'captacion' | 'cartera'
  eyebrow: string
  total: number
  subtitulo: string
  items: Array<{ label: string; monto: number; pct: number; tono: string; hint?: string }>
}) {
  const isCartera = tono === 'cartera'
  return (
    <div style={{
      padding: '18px 20px',
      borderRadius: 16,
      background: 'rgba(255,255,255,0.7)',
      border: '1px solid var(--hairline)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', flexDirection: 'column', gap: 14,
    }}>
      {/* Header bloque */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <div>
          <p style={{
            margin: 0, fontSize: 9, fontWeight: 800, letterSpacing: 2,
            color: isCartera ? 'var(--warning, #d97706)' : 'var(--brand-700)',
            textTransform: 'uppercase',
          }}>
            {eyebrow}
          </p>
          <p style={{
            margin: '4px 0 0', fontSize: 24, fontWeight: 800, color: 'var(--ink)',
            letterSpacing: '-0.01em', fontFeatureSettings: '"tnum"',
          }}>
            {fmtMoneda(total)}
          </p>
        </div>
      </div>
      <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
        {subtitulo}
      </p>

      {/* Items por producto */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4 }}>
        {items.map((it, idx) => (
          <div key={idx} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              fontSize: 12,
            }}>
              <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{it.label}</span>
              <span style={{
                color: 'var(--ink)', fontWeight: 700,
                fontFeatureSettings: '"tnum"', letterSpacing: '-0.01em',
              }}>
                {fmtMoneda(it.monto)}
              </span>
            </div>
            <div style={{
              height: 4, borderRadius: 99,
              background: 'var(--n-100)', overflow: 'hidden',
            }}>
              <div style={{
                width: `${Math.max(it.pct, 2)}%`, height: '100%', borderRadius: 99,
                background: isCartera
                  ? 'linear-gradient(90deg, var(--warning, #d97706), var(--brand-500))'
                  : 'linear-gradient(90deg, var(--brand-500), var(--brand-700))',
                transition: 'width 0.4s ease-out',
              }} />
            </div>
            {it.hint && (
              <span style={{ fontSize: 10, color: 'var(--muted-foreground)' }}>
                {it.hint}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function SaldosBars({ saldos, total, variant }: { saldos: Array<[string, number]>; total: number; variant: 'onBrand' | 'onLight' }) {
  const isOnBrand = variant === 'onBrand'
  const trackBg = isOnBrand ? 'rgba(255,255,255,0.18)' : 'var(--n-100)'
  const fillBg  = isOnBrand ? 'rgba(255,255,255,0.85)' : 'var(--brand-600)'
  const labelOp = isOnBrand ? 0.85 : 0.75
  return (
    <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 7 }}>
      {saldos.map(([tipo, saldo]) => {
        const pct = (saldo / total) * 100
        return (
          <div key={tipo} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11 }}>
            <span style={{ width: 110, opacity: labelOp }}>{prettyTipoProducto(tipo)}</span>
            <div style={{ flex: 1, height: 5, borderRadius: 3, background: trackBg, overflow: 'hidden' }}>
              <div style={{ width: `${pct}%`, height: '100%', background: fillBg, borderRadius: 3 }} />
            </div>
            <span style={{ minWidth: 70, textAlign: 'right', fontWeight: 600, fontFeatureSettings: '"tnum"' }}>{fmtMoneda(saldo)}</span>
          </div>
        )
      })}
    </div>
  )
}
void SaldosBars  // legacy del hero anterior — disponible para reuso

// =============================================================
// ÚLTIMOS MOVIMIENTOS DE CUENTA — preview de los últimos 5 dentro
// del Resumen. Cierra el loop "registro un depósito → ¿dónde lo
// veo?". Para auditoría completa el admin va al feed de Caja, pero
// acá damos contexto inmediato para los saldos del hero.
// =============================================================
function UltimosMovimientosCuentaPreview({ movimientos }: { movimientos: Array<MovimientoCuentaListItem> }) {
  const top = movimientos.slice(0, 5)
  return (
    <div
      style={{
        padding: '22px 24px',
        borderRadius: 22,
        background: 'linear-gradient(135deg, #fff 0%, var(--brand-50) 100%)',
        border: '1px solid var(--hairline)',
        boxShadow: 'var(--shadow-sm)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute', bottom: -70, left: -70, width: 200, height: 200,
          borderRadius: '50%', background: 'var(--brand-100)', opacity: 0.35,
          filter: 'blur(40px)', pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div>
            <p style={{
              margin: 0, fontSize: 10, fontWeight: 800, letterSpacing: 2,
              color: 'var(--brand-700)', textTransform: 'uppercase',
            }}>
              Últimos movimientos
            </p>
            {top.length > 0 && (
              <p style={{
                margin: '4px 0 0', fontSize: 11, color: 'var(--ink-soft)',
                fontFeatureSettings: '"tnum"',
              }}>
                {top.length} de {movimientos.length} registrado{movimientos.length === 1 ? '' : 's'}
              </p>
            )}
          </div>
          {movimientos.length > 5 && (
            <Link
              to="/neocore/caja"
              style={{
                fontSize: 11, fontWeight: 700, color: 'var(--brand-700)',
                textDecoration: 'none', letterSpacing: 0.4,
              }}
            >
              Ver todos en caja →
            </Link>
          )}
        </div>
        {top.length === 0 ? (
          <Empty>
            Sin movimientos registrados todavía. Cuando registrés un depósito o retiro
            desde la fiche, va a aparecer acá con su correlativo INGECOP.
          </Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {top.map((m) => <MovimientoCuentaRow key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  )
}

function MovimientoCuentaRow({ m }: { m: MovimientoCuentaListItem }) {
  const [reimprimiendo, setReimprimiendo] = useState(false)
  const isAbono = Number(m.monto) > 0
  const isAnulado = m.caja_anulada
  const isReversa = !!m.reversa_de_id
  const isCredito = m.tipo_cuenta === 'credito'

  // Label corto del tipo de cuenta — distingue créditos (cobros de cuota)
  const cuentaLabel =
    m.tipo_cuenta === 'aportaciones' ? 'APORTACIONES'
    : m.tipo_cuenta === 'credito' ? 'CRÉDITO'
    : 'AHORRO'

  // Reimprimir comprobante — sólo aplica si tenemos el movimiento_caja origen
  // (los movimientos de reversa no tienen comprobante propio reimprimible).
  const puedeReimprimir = !!m.movimiento_caja_id && !isReversa
  const onReimprimir = async () => {
    if (!m.movimiento_caja_id) return
    setReimprimiendo(true)
    const res = await reimprimirComprobantePorMovimientoId(m.movimiento_caja_id)
    setReimprimiendo(false)
    if (!res.ok) alert(`No se pudo reimprimir: ${res.error}`)
  }

  // Color del dot — verde abono, brand egreso, gris anulado/reversa
  const dotColor = isAnulado || isReversa
    ? 'var(--n-300)'
    : isAbono
      ? 'var(--success-500, #16a34a)'
      : 'var(--brand-600)'

  const montoColor = isAnulado || isReversa
    ? 'var(--text-muted)'
    : isAbono
      ? 'var(--success-700, #15803d)'
      : 'var(--ink)'

  // Serie INGECOP por código de movimiento de caja
  const serie = serieDeTipoCodigo(m.caja_tipo_codigo)
  const correlativoLabel = serie && m.caja_correlativo
    ? `${serie}-${String(m.caja_correlativo).padStart(4, '0')}`
    : null

  return (
    <div
      className="card"
      style={{
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        opacity: isAnulado || isReversa ? 0.6 : 1,
      }}
    >
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 0.5,
              padding: '2px 6px',
              borderRadius: 4,
              background: isCredito ? 'var(--brand-50)' : 'var(--n-100)',
              color: isCredito ? 'var(--brand-700)' : 'var(--n-600)',
            }}
          >
            {cuentaLabel}
          </span>
          {isCredito && m.credito_id ? (
            <Link
              to={`/neocore/creditos/${m.credito_id}`}
              style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--brand-600)' }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'var(--ink)' }}
            >
              {m.descripcion ?? 'Pago de crédito'} →
            </Link>
          ) : (
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
              {m.descripcion ?? (isAbono ? 'Depósito' : 'Retiro')}
            </span>
          )}
          {(isAnulado || isReversa) && (
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-600)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {isReversa ? 'Reversa' : 'Anulado'}
            </span>
          )}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, display: 'flex', gap: 8 }}>
          {correlativoLabel && (
            <span style={{ fontFeatureSettings: '"tnum"', fontWeight: 500 }}>{correlativoLabel}</span>
          )}
          {correlativoLabel && <span>·</span>}
          <span>{fmtFechaHora(m.fecha)}</span>
          <span>·</span>
          <span>{fmtRelativo(m.fecha)}</span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: montoColor,
            fontFeatureSettings: '"tnum"',
            textDecoration: isAnulado || isReversa ? 'line-through' : 'none',
            textAlign: 'right',
          }}
        >
          {isAbono ? '+' : '−'}{fmtMoneda(Math.abs(Number(m.monto)))}
        </div>

        {puedeReimprimir && (
          <button
            type="button"
            onClick={onReimprimir}
            disabled={reimprimiendo}
            title="Reimprimir comprobante"
            aria-label="Reimprimir comprobante"
            style={{
              all: 'unset',
              cursor: reimprimiendo ? 'wait' : 'pointer',
              width: 28, height: 28,
              borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--n-500)',
              transition: 'background 120ms ease, color 120ms ease',
            }}
            onMouseEnter={(e) => {
              if (reimprimiendo) return
              e.currentTarget.style.background = 'var(--n-100)'
              e.currentTarget.style.color = 'var(--brand-600)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent'
              e.currentTarget.style.color = 'var(--n-500)'
            }}
          >
            <Printer size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  )
}

/** Determina la serie INGECOP a partir del código de movimiento de caja. */
function serieDeTipoCodigo(codigo: string | null): string | null {
  if (!codigo) return null
  // INGRESO codes start with 'E' → RIG; EGRESO with 'S' → REG
  if (codigo.startsWith('E')) return 'RIG'
  if (codigo.startsWith('S')) return 'REG'
  return null
}

function prettyEvento(e: string): string {
  const map: Record<string, string> = {
    PEP: 'persona expuesta políticamente',
    CPE: 'cliente públicamente expuesto',
    FAMILIAR_PEP: 'familiar de PEP',
    EXTRANJERO: 'residencia extranjera',
    MONTO_EXCEDIDO: 'umbral de monto excedido',
    JURIDICO: 'persona jurídica',
    MANUAL: 'decisión manual de cumplimiento',
  }
  return map[e] ?? e
}

/**
 * Descarga el expediente del asociado en JSON con la estructura del portal
 * oficial IVE — la misma forma que tiene el formulario en
 * https://portalpo.sib2.gob.gt/scaiapp/simplificado.
 *
 * Detecta automáticamente el tipo:
 *   - Si el asociado tiene `feic_extension` → exporta como FEIC (expediente
 *     ampliado). Incluye todo lo de FEIS más la sección "ampliacion".
 *   - Si solo tiene FEIS → exporta como FEIS.
 *
 * Estructura por sección:
 *   1) informacion_general  — lugar y fecha + datos personales + dirección
 *   2) informacion_economica — timeline INICIAL + ACTUALIZACIONES
 *   3) productos_y_servicios — array de productos con firmantes / tarjetahabientes / beneficiarios
 *
 * + metadata + (si FEIC) ampliacion + (siempre) auditoria interna NeoCore
 *   con histórico de cambios para trazabilidad regulatoria.
 *
 * Nombre archivo: FEIS_{no_asociado}_{YYYYMMDD}.json o FEIC_{no_asociado}_{YYYYMMDD}.json
 */
function descargarExpedienteIveJson(data: AsociadoCompleto) {
  const { asociado, feis, feis_actividad, feis_productos, feic_extension, feic_promociones } = data
  const now = new Date()
  const stamp = fechaGT(now).replace(/-/g, '')
  const tipoExpediente: 'FEIS' | 'FEIC' = feic_extension ? 'FEIC' : 'FEIS'

  // Helper: parte la dirección en componentes según el portal IVE
  const informacionGeneral = feis ? {
    lugar_y_fecha: {
      pais: feis.pais_residencia ?? 'GT',
      departamento: feis.departamento ?? null,
      municipio: feis.municipio ?? null,
      fecha_captura: feis.fecha_captura,
    },
    datos_personales: {
      primer_nombre: feis.primer_nombre,
      segundo_nombre: feis.segundo_nombre,
      otros_nombres: null,
      primer_apellido: feis.primer_apellido,
      segundo_apellido: feis.segundo_apellido,
      apellido_casada: feis.apellido_casada,
      tipo_documento: feis.tipo_documento,
      numero_documento: feis.numero_documento,
      vigencia_documento: feis.vigencia_documento,
      fecha_nacimiento: feis.fecha_nacimiento,
      lugar_nacimiento: feis.lugar_nacimiento,
      pais_nacimiento: feis.pais_nacimiento,
      nacionalidad: feis.nacionalidad,
      sexo: feis.genero,
      estado_civil: feis.estado_civil,
    },
    direccion_residencia: {
      pais: feis.pais_residencia,
      departamento: feis.departamento,
      municipio: feis.municipio,
      zona: feis.zona,
      colonia: feis.colonia,
      direccion_completa: feis.direccion_completa,
    },
    telefonos: feis.telefono ? [{ tipo: 'principal', numero: feis.telefono }] : [],
  } : null

  const informacionEconomica = (feis_actividad ?? []).map((act) => ({
    tipo: act.tipo,
    numero_orden: act.numero_orden,
    fecha: act.fecha,
    origen_ingresos: act.origen_ingresos_texto,
    monto_ingresos: act.monto_ingresos,
  }))

  const productosYServicios = (feis_productos ?? []).map((p) => ({
    lugar_y_fecha_adquisicion: {
      pais: p.lugar_pais,
      departamento: p.lugar_departamento,
      municipio: p.lugar_municipio,
      fecha: p.fecha_adquisicion,
    },
    tipo_producto: p.tipo_producto,
    nombre_descripcion: p.nombre_descripcion,
    identificador: p.identificador,
    moneda: p.moneda,
    valor: p.valor,
    estado: p.estado,
    firmantes: (p.firmantes ?? []).map((f) => ({
      tipo_documento: f.tipo_documento,
      numero_documento: f.numero_documento,
      nombre_completo: [f.primer_nombre, f.segundo_nombre, f.primer_apellido, f.segundo_apellido].filter(Boolean).join(' '),
      nacionalidad: f.nacionalidad,
      desde: f.desde,
      hasta: f.hasta,
    })),
    tarjetahabientes: (p.tarjetahabientes ?? []).map((t) => ({
      tipo_documento: t.tipo_documento,
      numero_documento: t.numero_documento,
      nombre_completo: [t.primer_nombre, t.segundo_nombre, t.primer_apellido, t.segundo_apellido].filter(Boolean).join(' '),
      numero_tarjeta_enmascarado: t.numero_tarjeta_enmascarado,
    })),
    beneficiarios: (p.beneficiarios ?? []).map((b) => ({
      tipo_persona: b.tipo_persona,
      nombre_completo: b.tipo_persona === 'JURIDICA'
        ? b.razon_social
        : [b.primer_nombre, b.segundo_nombre, b.primer_apellido, b.segundo_apellido].filter(Boolean).join(' '),
      tipo_documento: b.tipo_documento,
      numero_documento: b.numero_documento,
      porcentaje: b.porcentaje,
    })),
  }))

  // Sección FEIC (solo si aplica)
  const ampliacionFeic = feic_extension ? {
    persona_individual: {
      patrimonio_aproximado: feic_extension.patrimonio_aproximado,
      patrimonio_descripcion: feic_extension.patrimonio_descripcion,
      cuenta_otra_institucion: feic_extension.cuenta_otra_institucion,
      otras_instituciones: feic_extension.otras_instituciones,
      referencias: feic_extension.referencias,
    },
    persona_juridica: {
      razon_social: feic_extension.razon_social,
      nombre_comercial: feic_extension.nombre_comercial,
      rtu: feic_extension.rtu,
      escritura_constitucion: feic_extension.escritura_constitucion,
      fecha_constitucion: feic_extension.fecha_constitucion,
      notario_constitucion: feic_extension.notario_constitucion,
      representante_legal: {
        dpi: feic_extension.representante_legal_dpi,
        nombre: feic_extension.representante_legal_nombre,
        cargo: feic_extension.representante_legal_cargo,
        vigencia: feic_extension.representante_legal_vigencia,
      },
    },
    beneficiario_final_ubo: {
      nombre: feic_extension.ubo_nombre,
      dpi: feic_extension.ubo_dpi,
      porcentaje: feic_extension.ubo_porcentaje,
      tipo_control: feic_extension.ubo_tipo_control,
    },
    historico_promociones: feic_promociones ?? [],
  } : null

  const dump = {
    _metadata: {
      tipo_expediente: tipoExpediente,
      sistema: 'Neocoop NeoCore',
      formato_version: '2.0',
      generado_at: now.toISOString(),
      generado_por: 'NeoCore admin',
      proposito: `Expediente ${tipoExpediente} para entrega a IVE — Intendencia de Verificación Especial (Guatemala)`,
      asociado_no: asociado.no_asociado,
      version_feis: feis?.version ?? null,
      version_feic: feic_extension?.version ?? null,
    },

    informacion_general: informacionGeneral,
    informacion_economica: informacionEconomica,
    productos_y_servicios: productosYServicios,

    ...(tipoExpediente === 'FEIC' && { ampliacion: ampliacionFeic }),

    // Auditoría operativa NeoCore — útil para que IVE vea trazabilidad
    auditoria_neocore: {
      historico_cambios_feis: data.feis_historico ?? [],
      historico_estado_asociado: data.estado_historico,
      kyc_vigente: data.ultimo_kyc ? {
        provider: data.ultimo_kyc.provider,
        estado: data.ultimo_kyc.estado,
        decision_at: data.ultimo_kyc.decision_at,
        documentos: {
          dpi_frente: data.ultimo_kyc.dpi_frente_url,
          dpi_dorso: data.ultimo_kyc.dpi_dorso_url,
          recibo_servicios: data.ultimo_kyc.recibo_servicios_url,
        },
      } : null,
    },
  }

  const blob = new Blob([JSON.stringify(dump, null, 2)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${tipoExpediente}_${asociado.no_asociado}_${stamp}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

function FeicTab({ feic, requiereFeic }: { feic: AsociadoCompleto['feic']; requiereFeic: boolean }) {
  if (!feic) {
    return (
      <Empty>
        {requiereFeic
          ? 'El asociado requiere FEIC pero todavía no se ha capturado.'
          : 'No requiere FEIC. (Solo asociados PEP/CPE/jurídicos/extranjeros o que excedan umbrales.)'}
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" disabled>Iniciar captura FEIC</button>
        </div>
      </Empty>
    )
  }
  return (
    <div className="ficha-grid">
      <Section title="Motivo">
        <KVTable rows={[
          ['Motivo', capitalize(feic.motivo.replace(/_/g, ' ').toLowerCase())],
          ['Detalle', feic.motivo_detalle ?? '—'],
        ]} />
      </Section>
      {feic.tipo_persona === 'JURIDICA' && (
        <Section title="Persona jurídica">
          <KVTable rows={[
            ['Razón social', feic.razon_social ?? '—'],
            ['Nombre comercial', feic.nombre_comercial ?? '—'],
            ['NIT', feic.nit ?? '—'],
            ['Constitución', fmtFecha(feic.fecha_constitucion)],
          ]} />
        </Section>
      )}
      <Section title="Operación esperada">
        <KVTable rows={[
          ['Monto promedio', fmtMoneda(feic.monto_promedio_operacion)],
          ['Frecuencia', capitalize(feic.frecuencia_operaciones ?? '—')],
          ['Operaciones/año', feic.numero_operaciones_estimado ?? '—'],
        ]} />
      </Section>
      <Section title="Origen y destino">
        <KVTable rows={[
          ['Origen detalle', feic.origen_fondos_detalle ?? '—'],
          ['País origen', feic.pais_origen_fondos ?? '—'],
          ['Destino fondos', feic.destino_fondos ?? '—'],
        ]} />
      </Section>
      <Section title="Riesgo">
        <KVTable rows={[
          ['Calificación', capitalize(feic.riesgo_calificado ?? '—')],
          ['Fecha', fmtFechaHora(feic.fecha_calificacion_riesgo)],
        ]} />
      </Section>
      <Section title="Auditoría">
        <KVTable rows={[
          ['Versión', `v${feic.version}`],
          ['Capturado', fmtFechaHora(feic.fecha_captura)],
          ['Verificado', fmtFechaHora(feic.verificado_at)],
        ]} />
      </Section>
    </div>
  )
}

function ContactoTab({ c, asociadoId, onChange }: { c: AsociadoCompleto['contactabilidad']; asociadoId: string; onChange: () => void }) {
  const [editar, setEditar] = useState<ContactoSeccion | null>(null)

  // Si el asociado no tiene row de contactabilidad, igual permitimos crear desde acá:
  // el RPC actualizar_contactabilidad hace upsert.
  const empty = !c

  return (
    <>
      {empty && (
        <div style={{ padding: '12px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.5 }}>
          Este asociado todavía no tiene contactabilidad cargada. Tocá "Editar" en cualquier sección para empezar.
        </div>
      )}
      <div className="ficha-grid">
        <Section title="Email" onEdit={() => setEditar('email')}>
          <KVTable rows={[
            ['Principal', c?.email_principal ?? '—'],
            ['Secundario', c?.email_secundario ?? '—'],
          ]} />
        </Section>
        <Section title="Teléfonos" onEdit={() => setEditar('telefonos')}>
          <KVTable rows={[
            ['Móvil', c?.telefono_movil_e164 ?? c?.telefono_movil ?? '—'],
            ['Casa', c?.telefono_casa ?? '—'],
            ['Trabajo', c?.telefono_trabajo ?? '—'],
          ]} />
        </Section>
        <Section title="Opt-ins" onEdit={() => setEditar('optins')}>
          <KVTable rows={[
            ['WhatsApp', <OptInBadge on={!!c?.whatsapp_optin} />],
            ['SMS', <OptInBadge on={!!c?.sms_optin} />],
            ['Email', <OptInBadge on={!!c?.email_optin} />],
            ['Push', <OptInBadge on={!!c?.push_optin} />],
          ]} />
        </Section>
        <Section title="Emergencia" onEdit={() => setEditar('emergencia')}>
          <KVTable rows={[
            ['Nombre', c?.emergencia_nombre ?? '—'],
            ['Teléfono', c?.emergencia_telefono ?? '—'],
            ['Relación', capitalize(c?.emergencia_relacion ?? '—')],
          ]} />
        </Section>
        <Section title="Preferencias" onEdit={() => setEditar('preferencias')}>
          <KVTable rows={[
            ['Idioma', c?.idioma_preferencia ?? 'es-GT'],
            ['Zona horaria', c?.zona_horaria ?? 'America/Guatemala'],
          ]} />
        </Section>
      </div>

      <EditarContactoSeccionModal
        open={editar !== null}
        onClose={() => setEditar(null)}
        asociadoId={asociadoId}
        seccion={editar}
        contacto={c}
        onSuccess={onChange}
      />
    </>
  )
}

function BeneficiariosTab({
  list, asociadoId, requiereFeic, onChange,
}: {
  list: AsociadoCompleto['beneficiarios']
  asociadoId: string
  requiereFeic: boolean
  onChange: () => void
}) {
  const [agregarOpen, setAgregarOpen] = useState(false)
  const [revocando, setRevocando] = useState<AsociadoCompleto['beneficiarios'][number] | null>(null)

  const total = list.reduce((acc, b) => acc + Number(b.porcentaje), 0)
  const completo = total === 100
  const sobreasignado = total > 100

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            {list.length === 0
              ? 'No hay beneficiarios designados.'
              : `${list.length} beneficiario${list.length === 1 ? '' : 's'} vigente${list.length === 1 ? '' : 's'}.`}
          </p>
          {list.length > 0 && (
            <p style={{ fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              Total asignado:{' '}
              <strong
                style={{
                  fontWeight: 700,
                  color: completo ? '#047857' : sobreasignado ? '#b91c1c' : '#b45309',
                }}
              >
                {total.toFixed(0)}%
              </strong>
              {!completo && total < 100 && (
                <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                  {' '}— queda {(100 - total).toFixed(0)}% por asignar
                </span>
              )}
              {sobreasignado && (
                <span style={{ color: '#b91c1c', fontWeight: 400 }}>
                  {' '}— sobreasignado
                </span>
              )}
            </p>
          )}
        </div>

        <button
          className="btn btn-primary"
          onClick={() => setAgregarOpen(true)}
          disabled={total >= 100}
          title={total >= 100 ? 'Ya hay 100% asignado. Revocá uno antes de agregar otro.' : ''}
        >
          + Agregar beneficiario
        </button>
      </div>

      {list.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Nombre</th>
                  <th>Relación</th>
                  {requiereFeic && <th>DPI</th>}
                  <th>Teléfono</th>
                  <th className="num">Porcentaje</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {list.map((b) => (
                  <tr key={b.id}>
                    <td><div className="avatar-sm">{iniciales(b.nombre_completo)}</div></td>
                    <td style={{ fontWeight: 600 }}>{b.nombre_completo}</td>
                    <td>{capitalize(b.relacion)}</td>
                    {requiereFeic && (
                      <td className="mono" style={{ fontSize: 12 }}>
                        {b.numero_documento ? fmtDpi(b.numero_documento) : '—'}
                      </td>
                    )}
                    <td style={{ fontSize: 13 }}>{b.telefono ?? '—'}</td>
                    <td className="num" style={{ fontWeight: 700 }}>
                      {Number(b.porcentaje).toFixed(0)}%
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-ghost"
                        style={{ fontSize: 12, padding: '5px 10px' }}
                        onClick={() => setRevocando(b)}
                      >
                        Revocar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {list.length === 0 && (
        <Empty>
          La designación de beneficiarios es opcional. Cuando el asociado decida
          designarlos, agregalos aquí.
        </Empty>
      )}

      <BeneficiarioModal
        open={agregarOpen}
        onClose={() => setAgregarOpen(false)}
        asociadoId={asociadoId}
        requiereFeic={requiereFeic}
        porcentajeAsignado={total}
        onSuccess={onChange}
      />

      {revocando && (
        <RevocarBeneficiarioModal
          open={!!revocando}
          onClose={() => setRevocando(null)}
          beneficiarioId={revocando.id}
          beneficiarioNombre={revocando.nombre_completo}
          porcentaje={Number(revocando.porcentaje)}
          onSuccess={onChange}
        />
      )}
    </div>
  )
}

function KycTab({ kyc }: { kyc: AsociadoCompleto['ultimo_kyc'] }) {
  if (!kyc) {
    return (
      <Empty>
        Sin verificación KYC. El asociado fue creado por flujo manual o migración legacy.
        <div style={{ marginTop: 16 }}>
          <button className="btn btn-ghost" disabled>Iniciar verificación de identidad</button>
        </div>
      </Empty>
    )
  }
  return (
    <div className="ficha-grid">
      <Section title="Provider">
        <KVTable rows={[
          ['Proveedor', kyc.provider],
          ['Estado', <span className={`badge ${kycToneClass(kyc.estado)}`}>{kyc.estado}</span>],
          ['Iniciado', fmtFechaHora(kyc.iniciado_at)],
          ['Decidido', fmtFechaHora(kyc.decision_at)],
        ]} />
      </Section>
      <Section title="Scoring">
        <KVTable rows={[
          ['Match rostro', kyc.rostro_match_pct != null ? `${Number(kyc.rostro_match_pct).toFixed(1)}%` : '—'],
          ['Match datos', kyc.datos_match_pct != null ? `${Number(kyc.datos_match_pct).toFixed(1)}%` : '—'],
          ['RENAP', kyc.registro_renap_estado ?? '—'],
        ]} />
      </Section>
    </div>
  )
}

function DocumentosTab({
  kyc,
  asociadoId,
  onChanged,
}: {
  kyc: AsociadoCompleto['ultimo_kyc']
  asociadoId: string
  onChanged?: () => void
}) {
  // Estados: paths del KYC → signed URLs (TTL 5 min) servidos por Storage.
  // El bucket `kyc` es privado; por seguridad firmamos al vuelo en el cliente.
  const [signed, setSigned] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [errSign, setErrSign] = useState<string | null>(null)
  const [lightbox, setLightbox] = useState<{ src: string; label: string } | null>(null)

  // Reemplazo manual del recibo (operador recibe el correcto por otro canal).
  // Override local del path para reflejar el nuevo archivo sin esperar el refetch.
  const [reciboOverride, setReciboOverride] = useState<string | null>(null)
  const [reciboBusy, setReciboBusy] = useState(false)
  const [reciboMsg, setReciboMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const reciboInputRef = useRef<HTMLInputElement | null>(null)

  const slots = useMemo(() => {
    if (!kyc) return [] as Array<{ key: string; label: string; hint: string; path: string | null }>
    return [
      { key: 'dpi_frente', label: 'DPI · Frente',         hint: 'Lado con foto y datos',     path: kyc.dpi_frente_url },
      { key: 'dpi_dorso',  label: 'DPI · Dorso',          hint: 'Lado con CUI y MRZ',        path: kyc.dpi_dorso_url },
      { key: 'recibo',     label: 'Recibo de servicios',  hint: 'Comprobante de domicilio',  path: reciboOverride ?? kyc.recibo_servicios_url },
    ]
  }, [kyc, reciboOverride])

  const onReemplazarRecibo = async (file: File | null | undefined) => {
    if (!file) return
    setReciboMsg(null)

    const okType = /\.(jpe?g|png|webp|heic|heif|pdf)$/i.test(file.name) ||
      /^(image\/|application\/pdf)/.test(file.type)
    if (!okType) {
      setReciboMsg({ kind: 'err', text: 'Formato no válido — subí una imagen (JPG/PNG) o PDF.' })
      return
    }
    if (file.size > 15 * 1024 * 1024) {
      setReciboMsg({ kind: 'err', text: 'El archivo es muy grande (máx 15 MB).' })
      return
    }

    setReciboBusy(true)
    try {
      // Path nuevo dentro del bucket privado `kyc`. Se sube a una ruta distinta
      // a la original (no requiere policy de UPDATE en Storage) y luego el RPC
      // apunta el kyc_verification al nuevo archivo.
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase()
      const path = `manual/${asociadoId}/recibo_${Date.now()}.${ext}`

      const up = await supabase.storage.from('kyc').upload(path, file, {
        upsert: false,
        contentType: file.type || undefined,
      })
      if (up.error) throw new Error(up.error.message)

      const { data, error } = await supabase.rpc('reemplazar_recibo_kyc_admin', {
        p_asociado_id: asociadoId,
        p_storage_path: path,
      })
      if (error) throw new Error(error.message)
      void data

      // Firmar el nuevo archivo para verlo de una vez.
      const sign = await supabase.storage.from('kyc').createSignedUrl(path, 60 * 5)
      if (sign.data?.signedUrl) {
        setSigned((m) => ({ ...m, [path]: sign.data!.signedUrl }))
      }
      setReciboOverride(path)
      setReciboMsg({ kind: 'ok', text: 'Recibo reemplazado. La alerta quedó resuelta.' })
      onChanged?.()
    } catch (e) {
      setReciboMsg({ kind: 'err', text: e instanceof Error ? e.message : 'No se pudo reemplazar el recibo.' })
    } finally {
      setReciboBusy(false)
      if (reciboInputRef.current) reciboInputRef.current.value = ''
    }
  }

  useEffect(() => {
    let cancel = false
    if (!kyc) { setLoading(false); return }
    const paths = slots.map((s) => s.path).filter(Boolean) as string[]
    if (paths.length === 0) { setLoading(false); return }

    setLoading(true)
    setErrSign(null)
    supabase.storage.from('kyc').createSignedUrls(paths, 60 * 5)
      .then(({ data, error }) => {
        if (cancel) return
        if (error) { setErrSign(error.message); setLoading(false); return }
        const map: Record<string, string> = {}
        for (const r of data ?? []) {
          if (r.path && r.signedUrl) map[r.path] = r.signedUrl
        }
        setSigned(map)
        setLoading(false)
      })
    return () => { cancel = true }
  }, [kyc, slots])

  if (!kyc) {
    return (
      <Empty>
        Aún no hay documentos cargados. Se suben automáticamente cuando se
        completa la verificación KYC al alta del asociado.
      </Empty>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, lineHeight: 1.6, maxWidth: 640 }}>
        Documentos entregados durante la verificación KYC. Las imágenes se
        sirven con URL firmada temporal (5 min) — recargá si te quedaste fuera.
      </p>

      {errSign && <div className="error-banner" style={{ margin: 0 }}>{errSign}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 }}>
        {slots.map((s) => {
          const src = s.path ? signed[s.path] : null
          return (
            <div
              key={s.key}
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {(() => {
                const isPdf = !!s.path && s.path.toLowerCase().endsWith('.pdf')
                const wrapperStyle: React.CSSProperties = {
                  width: '100%',
                  aspectRatio: '3 / 4',
                  background: 'var(--bg)',
                  display: 'block',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                }
                if (loading) {
                  return (
                    <div style={{ ...wrapperStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
                      Cargando…
                    </div>
                  )
                }
                if (!src) {
                  return (
                    <div style={{ ...wrapperStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 12, padding: 16, textAlign: 'center' }}>
                      Sin archivo en este slot
                    </div>
                  )
                }
                if (isPdf) {
                  // Para PDFs: vista previa embebida + CTA "Abrir" arriba para
                  // que el operador pueda zoomear/scrollear en otra pestaña.
                  return (
                    <div style={{ position: 'relative', ...wrapperStyle }}>
                      <object
                        data={`${src}#toolbar=0&navpanes=0&view=FitH`}
                        type="application/pdf"
                        style={{ width: '100%', height: '100%', display: 'block' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)', fontSize: 12, padding: 16, textAlign: 'center' }}>
                          PDF — abrí en otra pestaña para verlo.
                        </div>
                      </object>
                      <a
                        href={src}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          position: 'absolute', top: 8, right: 8,
                          background: 'rgba(15, 18, 25, 0.85)', color: '#fff',
                          fontSize: 11, fontWeight: 600,
                          padding: '4px 10px', borderRadius: 999,
                          textDecoration: 'none',
                        }}
                      >
                        Abrir PDF
                      </a>
                    </div>
                  )
                }
                return (
                  <button
                    type="button"
                    onClick={() => setLightbox({ src, label: s.label })}
                    style={{ ...wrapperStyle, all: 'unset', display: 'block', width: '100%', aspectRatio: '3 / 4', background: 'var(--bg)', cursor: 'zoom-in' }}
                  >
                    <img
                      src={src}
                      alt={s.label}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                  </button>
                )
              })()}
              <div style={{ padding: '12px 14px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                  {s.hint}
                </div>

                {s.key === 'recibo' && (
                  <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input
                      ref={reciboInputRef}
                      type="file"
                      accept="image/*,application/pdf"
                      style={{ display: 'none' }}
                      onChange={(ev) => onReemplazarRecibo(ev.target.files?.[0])}
                    />
                    <button
                      type="button"
                      disabled={reciboBusy}
                      onClick={() => reciboInputRef.current?.click()}
                      style={{
                        all: 'unset',
                        cursor: reciboBusy ? 'default' : 'pointer',
                        textAlign: 'center',
                        fontSize: 12,
                        fontWeight: 600,
                        color: reciboBusy ? 'var(--text-muted)' : 'var(--brand-600)',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                        padding: '7px 10px',
                        opacity: reciboBusy ? 0.6 : 1,
                      }}
                    >
                      {reciboBusy ? 'Subiendo…' : 'Reemplazar recibo'}
                    </button>
                    {reciboMsg && (
                      <div
                        style={{
                          fontSize: 11,
                          lineHeight: 1.4,
                          color: reciboMsg.kind === 'ok' ? 'var(--success-600, #15803d)' : 'var(--danger-600, #b91c1c)',
                        }}
                      >
                        {reciboMsg.text}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15, 18, 25, 0.85)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: 32, cursor: 'zoom-out',
          }}
        >
          <img
            src={lightbox.src}
            alt={lightbox.label}
            style={{ maxWidth: '92vw', maxHeight: '92vh', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
          />
        </div>
      )}
    </div>
  )
}

type TimelineEvent =
  | { kind: 'estado'; at: string; data: AsociadoCompleto['estado_historico'][number] }
  | { kind: 'feis';   at: string; data: AsociadoCompleto['feis_historico'][number] }

function HistoricoTab({ estados, feis }: { estados: AsociadoCompleto['estado_historico']; feis: AsociadoCompleto['feis_historico'] }) {
  // Merge cronológico — el feed va de más reciente a más viejo.
  const events: TimelineEvent[] = [
    ...estados.map((e) => ({ kind: 'estado' as const, at: e.cambiado_at, data: e })),
    ...feis.map((f)    => ({ kind: 'feis'   as const, at: f.cambiado_at, data: f })),
  ].sort((a, b) => (a.at < b.at ? 1 : -1))

  if (events.length === 0) return <Empty>Sin actividad registrada todavía.</Empty>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
      {/* Línea vertical del timeline */}
      <div style={{ position: 'absolute', left: 11, top: 8, bottom: 8, width: 2, background: 'var(--border)' }} />

      {events.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 14, padding: '12px 0', position: 'relative' }}>
          {/* Punto del timeline */}
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'var(--surface)',
              border: `2px solid ${e.kind === 'estado' ? estadoDot((e.data as AsociadoCompleto['estado_historico'][number]).estado_nuevo) : 'var(--brand-600)'}`,
              flexShrink: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 10,
              fontWeight: 700,
              color: e.kind === 'estado' ? estadoDot((e.data as AsociadoCompleto['estado_historico'][number]).estado_nuevo) : 'var(--brand-600)',
            }}
          >
            {e.kind === 'estado' ? '●' : 'F'}
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            {e.kind === 'estado' ? <EstadoEvento h={e.data as AsociadoCompleto['estado_historico'][number]} /> : <FeisEvento h={e.data as AsociadoCompleto['feis_historico'][number]} />}
          </div>
        </div>
      ))}
    </div>
  )
}

function EstadoEvento({ h }: { h: AsociadoCompleto['estado_historico'][number] }) {
  return (
    <>
      <p style={{ fontSize: 14, margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>
        {h.estado_anterior
          ? <>Cambio de estado · <span style={{ color: 'var(--n-500)' }}>{capitalize(h.estado_anterior)}</span> → <span style={{ color: 'var(--text-primary)' }}>{capitalize(h.estado_nuevo)}</span></>
          : <>Estado inicial · {capitalize(h.estado_nuevo)}</>}
      </p>
      {h.motivo && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '4px 0 0', lineHeight: 1.5 }}>{h.motivo}</p>
      )}
      <p style={{ fontSize: 11, color: 'var(--n-400)', margin: '4px 0 0' }}>
        {fmtFechaHora(h.cambiado_at)} · {fmtRelativo(h.cambiado_at)}
      </p>
    </>
  )
}

function FeisEvento({ h }: { h: AsociadoCompleto['feis_historico'][number] }) {
  // Cuando el operador cambia 5 campos de una sentada, el trigger inserta 5
  // rows con la misma version_nueva. Este componente muestra cada cambio
  // en su propia línea — más adelante podemos colapsar grupos.
  return (
    <>
      <p style={{ fontSize: 14, margin: 0, fontWeight: 600, color: 'var(--text-primary)' }}>
        FEIS actualizado · <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{prettyFeisCampo(h.campo)}</span>
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 4 }}>
        <span style={{ fontSize: 12, color: 'var(--n-500)', textDecoration: 'line-through' }}>
          {h.valor_anterior ?? '—'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--n-400)' }}>→</span>
        <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, background: 'var(--brand-50)', padding: '2px 8px', borderRadius: 6 }}>
          {h.valor_nuevo ?? '—'}
        </span>
      </div>
      <p style={{ fontSize: 11, color: 'var(--n-400)', margin: '6px 0 0' }}>
        {fmtFechaHora(h.cambiado_at)} · {fmtRelativo(h.cambiado_at)} · por {h.cambiado_por_email} · v{h.version_anterior} → v{h.version_nueva}
      </p>
    </>
  )
}

// Mapeo de columnas DB → label legible para el feed
const FEIS_LABELS: Record<string, string> = {
  tipo_documento: 'Tipo de documento',
  numero_documento: 'Número de documento',
  vigencia_documento: 'Vigencia del documento',
  primer_nombre: 'Primer nombre',
  segundo_nombre: 'Segundo nombre',
  primer_apellido: 'Primer apellido',
  segundo_apellido: 'Segundo apellido',
  apellido_casada: 'Apellido de casada',
  fecha_nacimiento: 'Fecha de nacimiento',
  lugar_nacimiento: 'Lugar de nacimiento',
  pais_nacimiento: 'País de nacimiento',
  nacionalidad: 'Nacionalidad',
  genero: 'Género',
  estado_civil: 'Estado civil',
  direccion_completa: 'Dirección',
  zona: 'Zona',
  colonia: 'Colonia',
  municipio: 'Municipio',
  departamento: 'Departamento',
  pais_residencia: 'País de residencia',
  telefono: 'Teléfono',
  profesion_oficio: 'Profesión / oficio',
  actividad_economica: 'Actividad económica',
  origen_fondos: 'Origen de fondos',
  ingresos_mensuales_aprox: 'Ingresos mensuales',
  proposito_relacion: 'Propósito de la relación',
  es_pep: 'Bandera PEP',
  es_cpe: 'Bandera CPE',
  es_familiar_pep: 'Bandera familiar PEP',
}
function prettyFeisCampo(c: string): string {
  return FEIS_LABELS[c] ?? c
}

// =============================================================
// Reusable bits
// =============================================================

function BackLink() {
  return (
    <Link to="/neocore/asociados" className="back-link">
      <CaretLeft size={14} weight="bold" /> Asociados
    </Link>
  )
}


function Section({ title, children, onEdit }: { title: string; children: ReactNode; onEdit?: () => void }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '0 0 10px' }}>
        <h3 style={{ fontSize: 11, fontWeight: 600, margin: 0, color: 'var(--n-500)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
          {title}
        </h3>
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            style={{
              all: 'unset',
              cursor: 'pointer',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--brand-600)',
              padding: '2px 6px',
              borderRadius: 4,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--brand-50)' }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent' }}
          >
            Editar
          </button>
        )}
      </div>
      {children}
    </div>
  )
}

function KVTable({ rows }: { rows: Array<[string, ReactNode]> }) {
  return (
    <table style={{ width: '100%', fontSize: 13 }}>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i}>
            <td style={{ padding: '4px 0', color: 'var(--n-500)', width: '45%', verticalAlign: 'top' }}>{k}</td>
            <td style={{ padding: '4px 0', textAlign: 'right' }}>{v ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function EstadoBadgeFicha({ estado }: { estado: AsociadoCompleto['asociado']['estado_asociado'] }) {
  if (estado === 'activo') {
    return (
      <span className="badge badge-success">
        <span className="badge-live-dot" />
        Activo
      </span>
    )
  }
  return (
    <span className={`badge ${estadoToneClass(estado)}`}>
      {capitalize(estado)}
    </span>
  )
}

function OptInBadge({ on }: { on: boolean }) {
  return <span className={`badge ${on ? 'badge-success' : 'badge-muted'}`}>{on ? 'Activo' : 'Off'}</span>
}

function BoolBadge({ value }: { value: boolean }) {
  return <span className={`badge ${value ? 'badge-warn' : 'badge-success'}`}>{value ? 'Sí' : 'No'}</span>
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="ficha-empty">
      <div className="empty-icon">
        <Warning size={18} weight="bold" />
      </div>
      <div>{children}</div>
    </div>
  )
}

// helpers
function iniciales(nombre: string | null | undefined): string {
  if (!nombre) return '—'
  const STOP = new Set(['DE', 'DEL', 'LA', 'LAS', 'LOS', 'Y', 'DA', 'DI', 'VAN', 'VON'])
  const parts = nombre.trim().toUpperCase().split(/\s+/).filter((p) => p && !STOP.has(p))
  if (parts.length === 0) return '—'
  if (parts.length === 1) return parts[0].slice(0, 2)
  if (parts.length <= 3) return parts[0][0] + parts[1][0]
  const firstApellidoIdx = Math.floor(parts.length / 2)
  return parts[0][0] + parts[firstApellidoIdx][0]
}

function capitalize(s: string): string {
  if (!s) return ''
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
}

function fmtDpi(dpi: string | null | undefined): string {
  if (!dpi) return '—'
  if (dpi.length !== 13) return dpi
  return `${dpi.slice(0, 4)} ${dpi.slice(4, 9)} ${dpi.slice(9)}`
}

function fmtFecha(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('es-GT', {
      year: 'numeric', month: 'short', day: '2-digit',
    })
  } catch { return iso }
}

function fmtFechaHora(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('es-GT', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  } catch { return iso }
}

function fmtRelativo(iso: string | null | undefined): string {
  if (!iso) return '—'
  const ms = Date.now() - new Date(iso).getTime()
  const s = Math.round(ms / 1000)
  if (s < 60) return 'hace unos segundos'
  const m = Math.round(s / 60)
  if (m < 60) return `hace ${m} min`
  const h = Math.round(m / 60)
  if (h < 24) return `hace ${h} h`
  const d = Math.round(h / 24)
  if (d < 30) return `hace ${d} d`
  return fmtFecha(iso)
}

function fmtMoneda(n: number | null | undefined): string {
  if (n == null) return '—'
  return new Intl.NumberFormat('es-GT', {
    style: 'currency', currency: 'GTQ',
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  }).format(n)
}

function estadoToneClass(estado: EstadoAsociado): string {
  switch (estado) {
    case 'activo':     return 'badge-success'
    case 'pendiente':  return 'badge-warn'
    case 'prospecto':  return 'badge-info'
    case 'suspendido': return 'badge-danger'
    case 'retirado':   return 'badge-muted'
    default:           return 'badge-muted'
  }
}

function estadoDot(estado: EstadoAsociado): string {
  switch (estado) {
    case 'activo':     return '#22c55e'
    case 'pendiente':  return '#f59e0b'
    case 'prospecto':  return '#3b82f6'
    case 'suspendido': return '#ef4444'
    case 'retirado':   return '#a1a1aa'
    default:           return '#a1a1aa'
  }
}

function kycToneClass(estado: KycEstado): string {
  switch (estado) {
    case 'pass':      return 'badge-success'
    case 'review':    return 'badge-warn'
    case 'fail':      return 'badge-danger'
    case 'expirado':  return 'badge-danger'
    default:          return 'badge-info'
  }
}
